import {
  DonationFormCategory,
  FormType,
  Locales,
  TicketingFormCategory,
  OrganizationCountry,
  FeatureFlagValue,
} from "@simplyk/common";

import { GetCurrentUserFrontendObject } from "../contexts/types";

export interface IdentifyAmplitudeUserProps {
  currentUser: GetCurrentUserFrontendObject;
  organization?: GetCurrentUserFrontendObject["currentOrganization"] | null;
}

export const REFERRAL_PROMPT_AT_FIRST_PAYOUT_EXPERIENCE_VALUE = {
  [FeatureFlagValue.A]: "Referral prompt at first payout - Version A",
  [FeatureFlagValue.B]: "Referral prompt at first payout - Version B",
  [FeatureFlagValue.C]: "Referral prompt at first payout - Version C",
};

export enum AmplitudeEvents {
  RaffleStateBlockerDisplayed = "Raffle State Blocker Displayed",
  SweepstakesFreeEntryOpened = "Sweepstakes Free Entry Opened",
  SweepstakesFreeEntrySubmitted = "Sweepstakes Free Entry Submitted",
  SweepstakesFreeEntrySucceeded = "Sweepstakes Free Entry Succeeded",
  SweepstakesRulesExpanded = "Sweepstakes Rules Expanded",
  // Frontend
  "AutomaticRedirect" = "Automatic Redirection",
  "PageViewed" = "Page Viewed",
  "GrantFinderPageViewed" = "Grant Finder Page Viewed",
  "ThankYouPageViewed" = "Thank You Page Viewed",
  "DonorFormViewed" = "Donor Form Viewed",
  "DonationFrequencyTabSelected" = "Donation Frequency Tab Selected",
  "DonorFormAddOnsStepViewed" = "Donor Form Add-Ons Step Viewed",
  "RegisterPageViewed" = "Register Page Viewed",
  "SignUpLandingPageViewed" = "Sign Up Landing Page Viewed",
  "ConfirmDonation" = "DON-CONFIRM-DONATION",
  "ConfirmOrder" = "TICK-CONFIRM-ORDER",
  "SuccessSubscription" = "DON-SUCCESS-SUBSCRIPTION",
  "SuccessAnyDonation" = "DON-CONFIRMATION-ANY-DON",
  "SuccessCheque" = "DON-SUCCESS-CHEQUE",
  "SuccessTransfer" = "DON-SUCCESS-TRANSFER",
  "SubmitWithGoogleOrApplePay" = "DON-SUBMIT-APPLE-GOOGLE-PAY",
  "SubmitTicketingWithGoogleOrApplePay" = "TICK-SUBMIT-APPLE-GOOGLE-PAY",
  "ClickOnNextButtonInThanksProducts" = "DON-CLICK-NEXT-THANKS-PRODUCTS",
  "ClickOnTicketingButtonInThanksProducts" = "DON-CLICK-TICKETING-THANKS-PRODUCTS",
  "ClickOnDonationButtonInThanksProducts" = "DON-CLICK-DONATION-THANKS-PRODUCTS",
  "ClickOnNextButtonInThanksMessage" = "DON-CLICK-NEXT-THANKS-MESSAGE",
  "ClickOnNextButtonInTicketingThanksProducts" = "TICK-CLICK-NEXT-THANKS-PRODUCTS",
  "ClickOnTicketingButtonInTicketingThanksProducts" = "TICK-CLICK-TICKETING-THANKS-PRODUCTS",
  "ClickOnDonationButtonInTicketingThanksProducts" = "TICK-CLICK-DONATION-THANKS-PRODUCTS",
  "ClickOnNextButtonInTicketingThanksMessage" = "TICK-CLICK-NEXT-THANKS-MESSAGE",
  "ClickOnCopyLinkInReferrer" = "DON-CLICK-COPY-LINK-REFERRRER",
  "ClickOnSimplykHomePage" = "DON-CLICK-SIMPLYK-HOMEPAGE-REFERRER",
  "PostEventThankYouEmailDisplayed" = "POST_EVENT_THANK_YOU_EMAIL_DISPLAYED",
  "EligibilityCountryModalClicked" = "Eligibility Country Modal Clicked",
  "EligibilityCountryModalViewed" = "Eligibility Country Modal Viewed",
  "GenerateDescriptionCompleted" = "Generate Description Completed",

  /**
   * @description Event is fired anytime someone make an order that succeed
   * (including : normal order[cheque - transfer - CB] - manual order[dashboard] - free order)
   * @template TicketingConversion
   */
  "SuccessAnyOrder" = "TICK-SUCCESS-ANY-ORDER",

  /**
   * @description Event is fired anytime someone make an order by CB, with positive amount that succeed
   * (including : normal order [CB])
   */
  "SuccessOrder" = "TICK-SUCCESS-ORDER",

  /**
   * @description Event is fired anytime someone make an order without payment method, because of 0$ amount that succeed
   * (including : free order)
   */
  "SuccessFreeOrder" = "TICK-FREE-SUCCESS-ORDER",

  /**
   * @description Event is fired anytime someone make an order by transfer, with positive amount that succeed
   * (including : normal order [transfer])
   */
  "SuccessOrderByTransfer" = "TICK-SUCCESS-ORDER-BY-TRANSFER",

  /**
   * @description Event is fired anytime someone make an order by cheque, with positive amount that succeed
   * (including : normal order [cheque])
   */
  "SuccessOrderSubscription" = "TICK-SUCCESS-SUBSCRIPTION",
  "SuccessOrderByCheque" = "TICK-SUCCESS-ORDER-BY-CHEQUE",
  "OpenTicketingTicketDrawer" = "TICK-OPEN-TICKET-DRAWER",
  "OpenTicketingOrderDrawer" = "TICK-OPEN-ORDER-DRAWER",
  "DonorFormStepSubmitted" = "Donor Form Step Submitted",
  "PushMonthlyModalShown" = "Push Monthly Modal Shown",
  "PushMonthlyAccepted" = "Push Monthly Accepted",
  "PushMonthlyDismissed" = "Push Monthly Dismissed",
  "TicketSuggestionShown" = "Ticket Suggestion Shown",
  "TicketSuggestionResolved" = "Ticket Suggestion Resolved",
  "TicketOptionToggled" = "Ticket Option Toggled",
  "TicketOptionsExpanded" = "Ticket Options Expanded",
  "TicketCapacityWarningShown" = "Ticket Capacity Warning Shown",
  "TicketCapacityWarningResolved" = "Ticket Capacity Warning Resolved",
  "ValidateBuyerFormInTicketing" = "TICK-VALIDATE-BUYER-FORM",
  "FailOrderOnFullTicketing" = "TICK-FAIL-ORDER-FULL-TICKETING",
  "FailOrderOnFullTicketingByTransfer" = "TICK-FAIL-ORDER-FULL-TICKETING-BY-TRANSFER",
  "FailOrderOnFullTicketingByCheque" = "TICK-FAIL-ORDER-FULL-TICKETING-BY_CHEQUE",
  "ConfirmFreePayment" = "PAY-CONFIRM-FREE-PAYMENT",
  "FailOrderOnFullTicketingFreeOrder" = "TICK-FAIL-ORDER-FULL-TICKETING-FREE_ORDER",
  "OpenPadStripeModal" = "PAY-OPEN-PAD-STRIPE-MODAL",
  "SuccessPadStripeModal" = "PAY-SUCCESS-PAD-STRIPE-MODAL",
  "SuccessAchStripeModal" = "PAY-SUCCESS-ACH-STRIPE-MODAL",
  "FailedAchStripeConfirmation" = "PAY-FAILED-ACH-STRIPE-CONFIRMATION",
  "FailedPadStripeModal" = "PAY-FAILED-PAD-STRIPE-MODAL",
  "FailedAchStripeModal" = "PAY-FAILED-ACH-STRIPE-MODAL",
  "CloseAchStripeModal" = "CLOSE-ACH-STRIPE-MODAL",
  "OpenFormOnFundraisingParticipation" = "OPEN-FORM-ON-FUNDRAISING-PARTICIPATION",
  "EditFormOnFundraisingParticipation" = "EDIT-FORM-ON-FUNDRAISING-PARTICIPATION",
  // Dashboard
  "LoginOnV2" = "ORG-LOGIN-ON-V2",
  "ClickOnBankDetails" = "PAYOUT_DETAILS-CLICK-BANK_DETAILS",
  "OpenDonationFormEdition" = "ORG-DF-EDITION",
  "OpenDonationFormShare" = "ORG-DF-SHARE",
  "OpenDonationForm" = "ORG-DF-OPEN",
  "OpenTicketingEdition" = "ORG-TICK-EDITION",
  "OpenTicketingShare" = "ORG-TICK-SHARE",
  "OpenTicketing" = "ORG-TICK-OPEN",
  "SubmitAdministrativesInformations" = "ORG-NEW-DF-ADMINISTRATIVES_INFORMATIONS-SUBMIT",
  "SubmitAdvancedParameters" = "ORG-NEW-DF-ADVANCED_PARAMETERS-SUBMIT",
  "SubmitFormCustomization" = "ORG-NEW-DF-FORM_CUSTOMIZATION-SUBMIT",
  "SubmitFormCustomizationOnEdit" = "ORG-EDIT-DF-FORM_CUSTOMIZATION-SUBMIT",
  "OpenAdministrativesInformations" = "ORG-NEW-DF-ADMINISTRATIVES_INFORMATIONS-OPEN",
  "SubmitGeneralInformations" = "ORG-NEW-DF-GENERAL_INFORMATIONS-SUBMIT",
  "SubmitGeneralInformationsOnEdit" = "ORG-EDIT-DF-GENERAL_INFORMATIONS-SUBMIT",
  "ClickOnBankAccountShareOnDonationForm" = "ORG-NEW-DF-SHARE-CLICK_ON_BANK_ACCOUNT",
  "ClickOnBankAccountShareOnTicketingForm" = "ORG-NEW-TICK-SHARE-CLICK_ON_BANK_ACCOUNT",
  "SubmitTranslations" = "ORG-NEW-DF-TRANSLATIONS-SUBMIT",
  "SubmitGeneralInformationsTicketing" = "ORG-NEW-TICKETING-GENERAL_INFORMATIONS-SUBMIT",
  "SubmitRatesTicketing" = "ORG-NEW-TICKETING-RATES-SUBMIT",
  "SubmitEventCustomizationTicketing" = "ORG-NEW-TICKETING-EVENT_CUSTOMIZATION-SUBMIT",
  "SubmitAdvancedParametersTicketing" = "ORG-NEW-TICKETING-PAYMENT_PARAMETERS-SUBMIT",
  "ClickOnKnowMoreConversionRateOnSavedFeeInfoPage" = "ORG-SAVED-FEE-INFO-KNOW_MORE-CONVERSION_RATE-CLICK",
  "ClickOnKnowMoreFeesOnSavedFeeInfoPage" = "ORG-SAVED-FEE-INFO-KNOW_MORE-FEES-CLICK",
  "CopyLinkInSavedFeeInfoPage" = "ORG-SAVED-FEE-INFO-PAGE-COPY-LINK",
  "ClickOnKnowMoreConversionRateOnSavedFeeCongratulationPage" = "ORG-SAVED-FEE-CONGRATS-KNOW_MORE-CONVERSION_RATE-CLICK",
  "ClickOnKnowMoreFeesOnSavedFeeCongratulationPage" = "ORG-SAVED-FEE-CONGRATS-KNOW_MORE-FEES-CLICK",
  "OpenSavedFeeCongratulationPage" = "ORG-SAVED-FEE-CONGRATS-PAGE-MOUNT",
  "CopyLinkInSavedFeeCongratulationPage" = "ORG-SAVED-FEE-CONGRATS-PAGE-COPY-LINK",
  "SubmitTicketingTranslations" = "ORG-NEW-TICK-TRANSLATIONS-SUBMIT",
  "FailOrderOnFullTicketingManualDonation" = "TICK-FAIL-ORDER-FULL-TICKETING-MANUAL-DONATION",
  "SuccessOrderManualDonation" = "TICK-SUCCESS-ORDER-MANUAL-DONATION",
  "ClickOnUploadSignature" = "TICK-SHARE-CLICK_ON_UPLOAD_SIGNATURE",
  "OpenMenuDonations" = "MENU-OPEN-DONATIONS",
  "OpenMenTickets" = "MENU-OPEN-TICKETS",
  "OpenSubMenuDonations" = "SUB-MENU-OPEN-DONATIONS",
  "OpenSubMenuDonationForms" = "SUB-MENU-OPEN-DONATION-FORMS",
  "OpenSubMenuTicketingForm" = "SUB-MENU-OPEN-TICKETING-FORMS",
  "OpenSubMenuTickets" = "SUB-MENU-OPEN-TICKETS",
  "OpenSubMenuBank" = "SUB-MENU-OPEN-BANK",
  "OpenSubMenuPayouts" = "SUB-MENU-OPEN-PAYOUTS",
  "OpenSubMenuTransactions" = "SUB-MENU-OPEN-TRANSACTIONS",
  "OpenSubMenuBalance" = "SUB-MENU-OPEN-BALANCE",
  "OpenMenuReceipts" = "MENU-OPEN-RECEIPTS",
  "MenuHover" = "MENU_HOVER",
  "MenuClickCreateYourFirstForm" = "MENU_CLICK_CREATE-YOUR-FIRST-FORM",
  "BankSetupStarted" = "Bank Setup Started",
  "MenuClickAcceptPayments" = "MENU_CLICK_ACCEPT-YOUR-PAYMENTS",
  "MenuClickReceivePayouts" = "MENU_CLICK_RECEIVE-YOUR-FIRST-PAYOUT",
  "MenuClickAttendDemo" = "MENU-CLICK-ATTEND_DEMO",
  "OpenSubMenuReferral" = "NAVBAR-CLICK-REFERRALS",
  "OpenSubMenuFundraising" = "NAVBAR-CLICK-FUNDRAISING",
  "OpenSubMenuForms" = "NAVBAR-CLICK-FORMS",
  "OpenSubMenuHomePage" = "NAVBAR-CLICK-HOME-PAGE",
  "OpenSubMenuPayments" = "NAVBAR-CLICK-PAYMENTS",
  "GenerateTicketsExport" = "TICKETS-GENERATE-EXPORTS",
  "GenerateDonationsExport" = "DONATIONS-GENERATE-EXPORTS",
  "OpenManualOrder" = "MANUAL-ORDER-OPEN-MODAL",
  "SendTestEmailInModal" = "SEND-EMAIL-TEST-IN-MODAL",
  "AutomatedEmailTestButtonClicked" = "Automated Email Test Button Clicked",
  "SendEmailToParticipantInModal" = "SEND-EMAIL-TO-PARTICIPANT-IN-MODAL",
  "OpenSendEmailModalFromTicketTable" = "OPEN-SEND-EMAIL-MODAL-FROM-TICKET-TABLE",
  "OpenSendEmailModalFromContactTable" = "OPEN-SEND-EMAIL-MODAL-FROM-CONTACT-TABLE",
  "OpenSendEmailModalFromUserBoard" = "OPEN-SEND-EMAIL-MODAL-FROM-USER-BOARD",
  "OpenModalPickDonationFormForMagicalLink" = "OPEN-MODAL-MAGICAL-LINK-PICK-DONATION-FORM",
  "OpenPageGenerateReceiptFromUserReceiptTable" = "OPEN-RECEIPT-PAGE-FROM-USER-RECEIPT-TABLE",
  "ExportContactList" = "CONTACT-LIST-EXPORT",
  "FilterContactList" = "CONTACT-LIST-FILTER",
  "ExportPaymentList" = "PAYMENT-LIST-EXPORT",
  "FilterPaymentList" = "PAYMENT-LIST-FILTER",
  "ExportEventGuestList" = "EVENT-GUEST-LIST-EXPORT",
  "PaymentListFocusSearch" = "PAYMENT_LIST-FOCUS-SEARCH",
  "ContactListFocusSearch" = "CONTACT_LIST-FOCUS-SEARCH",
  "ContactListFilterClicked" = "Contact List Filter Clicked",
  "ContactListFilterApplied" = "Contact List Filter Applied",
  "ContactListFilterTypeDropdownOpened" = "Contact List Filter Type Dropdown Opened",
  "ContactListFilterTypeSelected" = "Contact List Filter Type Selected",
  "ContactListFilterSearched" = "Contact List Filter Searched",
  "ContactListFilterOperatorDropdownOpened" = "Contact List Filter Operator Dropdown Opened",
  "ContactListFilterOperatorSelected" = "Contact List Filter Operator Selected",
  "ContactListFilterValueControlOpened" = "Contact List Filter Value Control Opened",
  "ContactListFilterValueEntered" = "Contact List Filter Value Entered",
  "ContactListFilterRowDeleted" = "Contact List Filter Row Deleted",
  "ContactListFilterCombinatorChanged" = "Contact List Filter Combinator Changed",
  "ContactListStarted" = "Contact List Started",
  "ContactListTypeChooserShown" = "Contact List Type Chooser Shown",
  "ContactListTypeChosen" = "Contact List Type Chosen",
  "ContactListTypeChooserDismissed" = "Contact List Type Chooser Dismissed",
  "ContactListFillMethodChosen" = "Contact List Fill Method Chosen",
  "ContactListNameModalShown" = "Contact List Name Modal Shown",
  "ContactListShellDiscarded" = "Contact List Shell Discarded",
  "MemberListFilterClicked" = "Member List Filter Clicked",
  "MemberListFilterApplied" = "Member List Filter Applied",
  "TableManageColumnsClicked" = "Table Manage Columns Clicked",
  "TableColumnAdded" = "Table Column Added",
  "TableColumnHidden" = "Table Column Hidden",
  "TableColumnReordered" = "Table Column Reordered",
  "TableColumnSorted" = "Table Column Sorted",
  "TableColumnFilterClicked" = "Table Column Filter Clicked",
  "TableColumnsReset" = "Table Columns Reset",
  "TableViewed" = "Table Viewed",
  "TableExported" = "Table Exported",
  "TableFilterAdded" = "Table Filter Added",
  "TableFiltersApplied" = "Table Filters Applied",
  "MemberCardDownloaded" = "Member Card Downloaded",
  "MemberAutoRenewDeactivated" = "Member AutoRenew Deactivated",
  "ContactListDeleted" = "Contact List Deleted",
  "ContactListEdited" = "Contact List Edited",
  "ContactListCreated" = "Contact List Created",
  "ContactListViewed" = "Contact List Viewed",
  "ContactListContactsAdded" = "Contact List Contacts Added",
  "ContactListContactsRemoved" = "Contact List Contacts Removed",
  "ContactListReassignContactToLists" = "Contact List Reassign Contact To Lists",
  "NewsletterFormContactListAssociated" = "Newsletter Form Contact List Associated",
  "NewsletterFormContactListDisassociated" = "Newsletter Form Contact List Disassociated",
  "FormListFocusSearch" = "FORM_LIST-FOCUS-SEARCH",
  "PaymentListApplyDateRange" = "PAYMENT_LIST-APPLY-DATE_RANGE",
  "PaymentListFocusDateRange" = "PAYMENT_LIST-FOCUS-DATE_RANGE",
  "PaymentListApplyFormList" = "PAYMENT_LIST-APPLY-FORM_LIST",
  "PaymentListFocusFormList" = "PAYMENT_LIST-FOCUS-FORM_LIST",
  "PaymentListApplyEventDate" = "PAYMENT_LIST-APPLY-EVENT_DATE",
  "FormListFocusDateRange" = "FORM_LIST-FOCUS-DATE_RANGE",
  "FormListApplyDateRange" = "FORM_LIST-APPLY-DATE_RANGE",
  "PaymentListFocusEventDate" = "PAYMENT_LIST-FOCUS-EVENT_DATE",
  "HomePageFocusFormFilter" = "HOME-PAGE-FOCUS-FORM_LIST",
  "HomePageApplyFormFilter" = "HOME-PAGE-LIST-FORM_LIST",
  "HomePageFocusDateFilter" = "HOME-PAGE-FOCUS-DATE_RANGE",
  "HomePageApplyDateFilter" = "HOME-PAGE-LIST-DATE_RANGE",
  "HomePageClickExport" = "HOME-PAGE-CLICK-EXPORT",
  "HomePageClickFrequency" = "HOME-PAGE-CLICK-FREQUENCY",
  "HomePageClickSuggestedFollowUp" = "HOME-CLICK-SUGGESTED_FOLLOW_UP-VIEW_ALL",
  "OpenDrawerDetailOrder" = "OPEN-DRAWER-DETAIL-ORDER",
  "CopyMagicalLinkOnVisitorPage" = "COPY-MAGICAL-LINK-ON-VISITOR-PAGE",
  "OpenEmailModalOnVisitorPage" = "OPEN-EMAIL-MODAL-ON-VISITOR-PAGE",
  "OpenEmailModalOnHomePage" = "OPEN-EMAIL-MODAL-ON-HOME-PAGE",
  "OpenEmailModalOnContactPage" = "OPEN-EMAIL-MODAL-ON-CONTACT-PAGE",
  "OpenManualOrderDrawer" = "OPEN-MANUAL-ORDER-DRAWER",
  "PaymentCreationStarted" = "Payment Creation Started",
  "PaymentCreated" = "Payment Created",
  "InKindValueEntered" = "In-Kind Value Entered",
  "PaymentDeleted" = "Payment Deleted",
  "PaymentDetailsViewed" = "Payment Details Viewed",
  "PaymentBouncedEmailEditClicked" = "Payment Bounced Email Edit Clicked",
  "PaymentBouncedEmailEditCompleted" = "Payment Bounced Email Edit Completed",
  "SignInPageSwitchToSignUp" = "SIGN-IN-PAGE-SWITCH-TO-SIGN-UP",
  "SignInCheckEmail" = "SIGN-IN-CHECK-EMAIL",
  "SignInUserIsUnknown" = "SIGN-IN-USER-IS-UNKNOWN",
  "SignInUserIsRegistered" = "SIGN-IN-USER-IS-REGISTERED",
  "SignInUserIsFirstConnectedDonor" = "SIGN-IN-USER-IS-FIRST-CONNECTED-DONOR",
  "SignInUserIsFirstConnectedUserAfterDonationWithoutV1Password" = "SIGN-IN-USER-IS-FIRST-CONNECTED-USER-AFTER-DONATION-WITHOUT-V1-PASSWORD",
  "SignInUserIsFirstConnectedUserAfterDonationWithV1Password" = "SIGN-IN-USER-IS-FIRST-CONNECTED-USER-AFTER-DONATION-WITH-V1-PASSWORD",
  "SignInTryConnection" = "SIGN-IN-TRY-CONNECTION",
  "SignInConnectionSuccessful" = "SIGN-IN-CONNECTION-SUCCESSFUL",
  "SignInConnectionFailed" = "SIGN-IN-CONNECTION-FAILED",
  "SignInSendResetPasswordRequest" = "SIGN-IN-SEND-RESET-PASSWORD-REQUEST",
  "SignInSendResetPasswordRequestAgain" = "SIGN-IN-SEND-RESET-PASSWORD-REQUEST-AGAIN",
  "SignInSendResetPasswordOnDonorFirstConnection" = "SIGN-IN-SEND-RESET-PASSWORD-ON-DONOR-FIRST-CONNECTION",
  "SignInSendResetPasswordOnUserMigrated" = "SIGN-IN-SEND-RESET-PASSWORD-ON-USER-MIGRATED",
  "SignUpFailed" = "SIGN-UP-FAILED",
  "SignUpClickLogin" = "SIGN_UP-CLICK-LOGIN",
  "SignUpBackLanding" = "SIGN_UP-BACK-LANDING",
  "SignUpClickCapterraReviews" = "SIGN_UP-CLICK-CAPTERRA_REVIEWS",
  "SignUpFocusEmail" = "SIGN_UP-FOCUS-EMAIL",
  "SignUpMethodEmailSelected" = "SIGN_UP-METHOD-EMAIL_SELECTED",
  "SignUpFocusPhone" = "SIGN_UP-FOCUS-PHONE",
  "SignUpFocusOrgName" = "SIGN_UP-FOCUS-ORGNAME",
  "SignUpClickSearchAddEinManually" = "SIGN_UP-CLICK-SEARCH-ADD_EIN_MANUALLY",
  "AdministrativeInformationClickEditEin" = "ADMINISTRATIVE_INFORMATION-CLICK-EDIT_EIN",
  "AdministrativeInformationClickEditAddress" = "ADMINISTRATIVE_INFORMATION-CLICK-EDIT_ADDRESS",
  "SignUpFocusWebsite" = "SIGN_UP-FOCUS-WEBSITE",
  "SignUpClickCountry" = "SIGN_UP-CLICK-COUNTRY",
  "SignUpClickShowPassword" = "SIGN_UP-CLICK-SHOW_PASSWORD",
  "SignUpMethodChoiceExperiment" = "SIGN_UP-METHOD_CHOICE-EXPERIMENT",
  "ExperimentRegister" = "experiment_register",
  "OrganizationSignUpInputChanged" = "Organization Sign Up Input Changed",
  "OrganizationSignUpPageViewed" = "Organization Sign Up Page Viewed",
  "OrganizationSignUpSubmitted" = "Organization Sign Up Submitted",
  "SignUpCompleted" = "Sign Up Completed",
  "ResetPasswordTry" = "RESET-PASSWORD-TRY",
  "ResetPasswordFailed" = "RESET-PASSWORD-FAILED",
  "ResetPasswordSuccessful" = "RESET-PASSWORD-SUCCESSFUL",
  "ResetPasswordTryAgain" = "RESET-PASSWORD-TRY-AGAIN",
  "DashboardClickHelp" = "Help Center Viewed",
  "DashboardClickFeatureRequest" = "Feature Request Started",
  "DashboardOpenSettingSelect" = "DASHBOARD-OPEN-SETTING_SELECT",
  "DashboardClickSettings" = "DASHBOARD-CLICK-SETTING",
  "DashboardClickSwitchProfiles" = "DASHBOARD-CLICK-SWITCH_PROFILE",
  "DashboardClickCreateNewOrganization" = "DASHBOARD-CLICK-CREATE_A_NEW_ORGANIZATION",
  "SettingsClickCreateNewOrganization" = "SETTINGS-CLICK_CREATE_A_NEW_ORGANIZATION",
  "SettingsClickMyPersonalInformation" = "SETTINGS-CLICK-MY_PERSONAL_INFO",
  "SettingsClickChangeMyPassword" = "SETTINGS-CLICK-CHANGE_MY_PASSWORD",
  "SettingsClickOrganizationInformation" = "SETTINGS-CLICK-ORGANIZATION_INFORMATION",
  "SettingsClickOrganizationLogo" = "SETTINGS-CLICK-ORGNIZATION_LOGO",
  "SettingsClickUsersManagement" = "SETTINGS-CLICK-USERS_MANAGEMENT",
  "SettingsClickBankIdentity" = "SETTINGS-CLICK-BANK_IDENTITY",
  "SettingsClickBankAccount" = "SETTINGS-CLICK-BANK_ACCOUNT",
  "SettingsClickPayoutSchedule" = "SETTINGS-CLICK-PAYOUT_SCHEDULE",
  "SettingsClickAddMyAccountant" = "SETTINGS-CLICK-ADD_MY_ACCOUNTANT",
  "SettingsClickFunds" = "SETTINGS-CLICK-FUNDS",
  "SettingsClickApiKey" = "SETTINGS-CLICK-API_KEY",
  "SettingsClickIntegrations" = "SETTINGS-CLICK-INTEGRATIONS",
  "BankClickBankSettings" = "BANK-CLICK-BANK_SETTING",
  "ClickOnMyReferrals" = "REFER-CLICK-MY_REFERRALS",
  "ClickOnMyRefer" = "REFERRAL-CLICK-REFER",
  "ClickLearnMoreReferralSignUp" = "REFERRAL_SIGN_UP-CLICK-LEARN_MORE",
  "LearnMoreSignUpReferralClickOnOutStory" = "LEARN_MORE_SIGN_UP_REFERRAL-CLICK-OUR_STORY",
  "LearnMoreSignUpReferralClickOnHowWeMakeMoney" = "LEARN_MORE_SIGN_UP_REFERRAL-CLICK-HOW_MAKE_MONEY",
  "LearnMoreSignUpReferralClickOnOurGoal" = "LEARN_MORE_SIGN_UP_REFERRAL-CLICK-OUR_GOALS",
  "LearnMoreSignUpClickOnOutStory" = "LEARN_MORE_SIGN_UP-CLICK-OUR_STORY",
  "LearnMoreSignUpClickOnHowWeMakeMoney" = "LEARN_MORE_SIGN_UP-CLICK-HOW_MAKE_MONEY",
  "LearnMoreSignUpClickOnOurGoal" = "LEARN_MORE_SIGN_UP-CLICK-OUR_GOALS",
  "LearnMoreTipClickOnOutStory" = "LEARN_MORE_TIPS-CLICK-OUR_STORY",
  "LearnMoreTipClickOnHowContributionHelp" = "LEARN_MORE_TIPS-CLICK-HOW_CONTRIBUTION_HELP",
  "LearnMoreTipClickOnHowWeMakeMoney" = "LEARN_MORE_TIPS-CLICK-HOW_MAKE_MONEY",
  "LearnMoreTipClickOnOurGoal" = "LEARN_MORE_TIPS-CLICK-OUR_GOALS",
  "ClickOnGenerateReceiptPage" = " RECEIPTS_PAGE-CLICK-NEW_RECEIPT",
  "BankingInformationClickSkipForNow" = "BANKING_INFORMATION-CLICK-SKIP_FOR_NOW",
  "BankSetupStepSubmitted" = "Bank Setup Step Submitted",
  "BankSetupInputChanged" = "Bank Setup Input Changed",
  "BankSetupPageFAQExpanded" = "Bank Setup Page FAQ Expanded",
  "BankSetupPageViewed" = "Bank Setup Page Viewed",
  "BankSetupBannerClicked" = "Bank Setup Banner Clicked",
  "BankVerificationLearnHowClicked" = "Bank Verification Learn How Clicked",
  "BankSetupOrganizationInfo1Viewed" = "Bank Setup Organization Info 1 Viewed",
  "BankSetupOrganizationInfo2Viewed" = "Bank Setup Organization Info 2 Viewed",
  "BankSetupBankingInfo1Viewed" = "Bank Setup Banking Info 1 Viewed",
  "DonorClickDashboard" = "DONOR-CLICK-DASHBOARD",
  "DonorClickCreateNewOrganization" = "DONOR-CLICK-CREATE_NEW_ORGANIZATION",
  "DonorClickSwitchProfile" = "DONOR-CLICK-SWITCH_PROFILE",
  "ClickOnActivesDropdown" = "DONATION_FORMS-CLICK-ACTIVES_DROPDOWN",
  "ClickOnArchiveDropdown" = "DONATION_FORMS-CLICK-ARCHIVES_DROPDOWN",
  "SharePageCopyFormLink" = "SHARE_PAGE-COPY-FORM_LINK",
  "SharePageP2PCopyIndividualCampaignLink" = "SHARE_PAGE_P2P-COPY-INDIVIDUAL_CAMPAIGN_LINK",
  "SharePageP2PCopyTeamLink" = "SHARE_PAGE_P2P-COPY-TEAM_LINK",
  "SharePageCopyFormEmbedLink" = "SHARE_PAGE-COPY-FORM_EMBED_LINK",
  "SharePageCopyFormEmbedPopupLink" = "SHARE_PAGE-COPY-FORM_EMBED_POPUP_LINK",
  "SharePageCopyFormEmbedPopupScript" = "SHARE_PAGE-COPY-FORM_EMBED_POPUP_SCRIPT",
  "SharePageCopyThermometerLink" = "SHARE_PAGE-COPY-THERMOMETER_LINK",
  "SharePageCopyLeaderboardLink" = "SHARE_PAGE-COPY-LEADERBOARD_LINK",
  "SharePageCopyQRCode" = "SHARE_PAGE-COPY-QR_CODE",
  "SharePageClickCompose" = "SHARE_PAGE-CLICK-COMPOSE",
  "SharePageClickOpenTapToPay" = "SHARE_PAGE-CLICK-OPEN_TAP_TO_PAY",
  "SharePageClickOpenEmbed" = "SHARE_PAGE-CLICK-OPEN_EMBED",
  "SharePageClickOpenEmbedPopup" = "SHARE_PAGE-CLICK-OPEN_EMBED-POP-UP",
  "SharePageClickOpenQrCode" = "SHARE_PAGE-CLICK-OPEN_QR-CODE",
  "GeneralInformationClickDonationCampaign" = "GENERAL_INFORMATION-CLICK-DONATION_CAMPAIGN",
  "GeneralInformationClickPeerToPeer" = "GENERAL_INFORMATION-CLICK-PEER_TO_PEER",
  "GeneralInformationClickEvent" = "GENERAL_INFORMATION-CLICK-EVENT",
  "GeneralInformationClickMembership" = "GENERAL_INFORMATION-CLICK-MEMBERSHIP",
  "GeneralInformationClickLottery" = "GENERAL_INFORMATION-CLICK-LOTTERY",
  "GeneralInformationClickECommerce" = "GENERAL_INFORMATION-CLICK-E_COMMERCE",
  "GeneralInformationClickCustom" = "GENERAL_INFORMATION-CLICK-CUSTOM_FORM",
  "FormInputChanged" = "Form Input Changed",
  "GeneralInformationFocusTarget" = "GENERAL_INFORMATION-FOCUS-TARGET",
  "FormEventDateAdded" = "Form Event Date Added",
  "FormEventRecurrenceChanged" = "Form Event Recurrence Changed",
  "GeneralInformationClickAuction" = "GENERAL_INFORMATION-CLICK-AUCTION",
  "GeneralInformationClickAddATarget" = "GENERAL_INFORMATION-CLICK-ADD_A_TARGET",
  "CustomizationClickOneTimeSwitch" = "CUSTOMIZATION-CLICK-ONE_TIME_SWITCH",
  "CustomizationClickMonthlySwitch" = "CUSTOMIZATION-CLICK-MONTHLY_SWITCH",
  "CustomizationClickYearlySwitch" = "CUSTOMIZATION-CLICK-YEARLY_SWITCH",
  "CustomizationFocusAmount" = "CUSTOMIZATION-FOCUS-AMOUNT",
  "CustomizationFocusAmountDescription" = "CUSTOMIZATION-FOCUS-AMOUNT_DESCRIPTION",
  "CustomizationClickGenerateReceipts" = "CUSTOMIZATION-CLICK-GENERATE_RECEIPTS",
  "CustomizationClickFormColor" = "CUSTOMIZATION-CLICK-FORM_COLOR",
  "CustomizationClickFormBanner" = "CUSTOMIZATION-CLICK-FORM_BANNER",
  "CustomizationClickOrgLogo" = "CUSTOMIZATION-CLICK-LOGO_ORGANIZATION",
  "CustomizationFocusYoutubeVideo" = "CUSTOMIZATION-FOCUS-YOUTUBE_VIDEO",
  "CustomizationFocusLoserEmailSubject" = "CUSTOMIZATION-FOCUS-LOSER-EMAIL_SUBJECT",
  "CustomizationFocusLoserEmailDescription" = "CUSTOMIZATION-FOCUS-LOSER-EMAIL_DESCRIPTION",
  "CustomizationFocusCampaignEmailDescription" = "CUSTOMIZATION-FOCUS-CAMPAIGN_EMAIL_DESCRIPTION",
  "CustomizationFocusThankYouMessage" = "CUSTOMIZATION-FOCUS-THANK_YOU_MESSAGE",
  "CustomizationClickGenerateETicket" = "CUSTOMIZATION-CLICK-GENERATE_E_TICKETS",
  "CustomizationClickTicketBanner" = "CUSTOMIZATION-CLICK-TICKET_BANNER",
  "TicketsFocusBidIncrement" = "TICKETS-FOCUS-BID_INCREMENT",
  "FormTicketAdded" = "Form Ticket Added",
  "FormAddOnAdded" = "Form Add-on Added",
  "FormTicketQuestionAdded" = "Form Ticket Question Added",
  "CustomQuestionsModalOpened" = "Custom Questions Modal Opened",
  "ExistingCustomQuestionAdded" = "Existing Custom Question Added",
  "CustomQuestionCreationStarted" = "Custom Question Creation Started",
  "CustomQuestionCreated" = "Custom Question Created",
  "CustomQuestionEditionStarted" = "Custom Question Edition Started",
  "CustomQuestionEdited" = "Custom Question Edited",
  "CustomQuestionSearched" = "Custom Question Searched",
  "CommunicationPreferencesConfigured" = "Communication Preferences Configured",
  "GuestQuestionSuggested" = "Guest Question Suggested",
  "GuestQuestionSuggestionDismissed" = "Guest Question Suggestion Dismissed",
  "GuestQuestionAdded" = "Guest Question Added",
  "FormTicketQuestionChanged" = "Form Ticket Question Changed",
  "FormTicketQuestionDeleted" = "Form Ticket Question Deleted",
  "OrgPageEditorSubmitted" = "Org Page Editor Submitted",
  "OrgPageEditorBackClicked" = "Org Page Editor Back Clicked",
  "OrgPageDonationCampaignChanged" = "Org Page Donation Campaign Changed",
  "OrgPageSubscriptionChanged" = "Org Page Subscription Changed",
  "OrgPageMissionChanged" = "Org Page Mission Changed",
  "OrgPageHiddenCampaignsChanged" = "Org Page Hidden Campaigns Changed",
  "OrgPageOtherCampaignsChanged" = "Org Page Other Campaigns Changed",
  "OrgPageViewed" = "Org Page Viewed",
  "OrgPageCampaignOpened" = "Org Page Campaign Opened",
  "OrgPageDonationStarted" = "Org Page Donation Started",
  "OrgPageDonationCompleted" = "Org Page Donation Completed",
  "OrgPageSubscriptionStarted" = "Org Page Subscription Started",
  "OrgPagePastEventsViewed" = "Org Page Past Events Viewed",
  "OrgPageOpened" = "Org Page Opened",
  "OrgPageWebsiteOpened" = "Org Page Website Opened",
  "OrgPageAddressOpened" = "Org Page Address Opened",
  "OrgPageAddressCopied" = "Org Page Address Copied",
  "OrgPageContactUsClicked" = "Org Page Contact Us Clicked",
  "OrgPageContactMessageSent" = "Org Page Contact Message Sent",
  "TicketsClickChangeTicketQuestionType" = "TICKETS-CLICK-CHANGE_TICKET_QUESTION_TYPE",
  "TicketsClickChangeBuyerQuestionType" = "TICKETS-CLICK-CHANGE_BUYER_QUESTION_TYPE",
  "TicketsClickRateImage" = "TICKETS-CLICK-RATE_IMAGE",
  "FormExtraDonationAdded" = "Form Extra Donation Added",
  "TicketsClickQuestionMaximumChoice" = "TICKETS-CLICK-QUESTION_MAXIMUM_CHOICE",
  "TicketsClickPreviewReceipt" = "TICKETS-CLICK-PREVIEW_RECEIPT",
  "FormPreviewClickNext" = "FORM_PREVIEW-CLICK-NAVBAR_NEXT",
  "FormSaveModalViewed" = "Form Save Modal Viewed",
  "FormSaveModalDiscarded" = "Form Save Modal Discarded",
  "FormSaveModalSaved" = "Form Save Modal Saved",
  "DonorFormDescriptionOpened" = "Donor Form Description Opened",
  "DonorFormDonationChanged" = "Donor Form Donation Changed",
  "DonationFocusGeneralInput" = "DONATION-FOCUS-GENERAL_INPUT",
  "ManualOrderDrawerFocusPaymentMethod" = "MANUAL_ORDER_DRAWER-FOCUS-PAYMENT_METHOD",
  "ManualOrderDrawerSelectPaymentMethod" = "MANUAL_ORDER_DRAWER-SELECT-PAYMENT_METHOD",
  "DonorFormInputChanged" = "Donor Form Input Changed",
  "DonorFormTicketsCountChanged" = "Donor Form Tickets Count Changed",
  "DonorFormExtraDonationChanged" = "Donor Form Extra Donation Changed",
  "DonorFormDiscountCodeChecked" = "Donor Form Discount Code Checked",
  "NewDonationPillDisplayed" = "New Donation Pill Displayed",
  "ThermometerContributionPreviewed" = "Thermometer Contribution Previewed",
  "TicketClickGeneralInput" = "TICKET-CLICK-GENERAL_INPUT",
  "TicketClickDateSelect" = "TICKET-CLICK-DATE_SELECT",
  "FormFocusCustomInput" = "FORM-FOCUS-CUSTOM_INPUT",
  "FormClickCustomInput" = "FORM-CLICK-CUSTOM_INPUT",
  "FormClickTipDropdown" = "Donor Form Tip Dropdown Opened",
  "DonorFormTipChanged" = "Donor Form Tip Changed",
  "FormClickTipKnowMore" = "FORM-CLICK-TIP_KNOW_MORE",
  "DonorFormSubmitted" = "Donor Form Submitted",
  "FormClickShowOtherPaymentMethods" = "FORM-CLICK-SHOW_OTHER_PAYMENT_METHODS",
  "FormFocusCardInput" = "FORM-FOCUS-CARD_INPUT",
  "PaymentListNewMessage" = "PAYMENT_LIST-CLICK-NEW_MESSAGE",
  "ContactListTableMessage" = "CONTACT_LIST_TABLE-CLICK-MESSAGE",
  "EmailEditorClickClose" = "EMAIL_EDITOR-CLICK-CLOSE",
  "EmailEditorClickContactList" = "EMAIL_EDITOR-CLICK-CONTACT_LIST",
  "EmailEditorAddVariableClicked" = "Email Editor Add Variable Clicked",
  "EmailEditorClickAddTemplate" = "EMAIL_EDITOR-CLICK-ADD_TEMPLATE",
  "EmailEditorClickAddAttachment" = "EMAIL_EDITOR-CLICK-ADD_ATTACHMENT",
  "EmailEditorTemplateClickNew" = "EMAIL_EDITOR_TEMPLATE-CLICK-NEW",
  "EmailEditorTemplateClickSave" = "EMAIL_EDITOR_TEMPLATE-CLICK-SAVE",
  "EmailEditorTemplateClickSaveAsNew" = "EMAIL_EDITOR_TEMPLATE-CLICK-SAVE_AS_NEW",
  "EmailEditorTemplateClickDelete" = "EMAIL_EDITOR_TEMPLATE-CLICK-DELETE",
  "EmailEditorTemplateClickSelectATemplate" = "EMAIL_EDITOR_TEMPLATE-CLICK-SELECT_A_TEMPLATE",
  "EmailEditorPrefillTextClicked" = "Email Editor Prefill Text Clicked",
  "EmailSendConfirmationClickCancel" = "EMAIL_SEND_CONFIRMATION-CLICK-CANCEL",
  "EmailSent" = "Email Sent",
  "EmailSendFailed" = "Email Send Failed",
  "EmailSendCtaViewed" = "Email Send CTA Viewed",
  "CopyEmailInfoboxCloseClicked" = "Copy Email Infobox Close Clicked",
  "CopyEmailInfoboxDismissClicked" = "Copy Email Infobox Dismiss Clicked",
  "CopyEmailInfoboxOpenEmailingClicked" = "Copy Email Infobox OpenEmailing Clicked",
  "ContactPageClickNewMessage" = "CONTACT_PAGE-CLICK-NEW_MESSAGE",
  "TicketsListClickNewMessage" = "TICKETS_LIST-CLICK-NEW_MESSAGE",
  "VariableListClickDate" = "VARIABLE_LIST-CLICK-DATE",
  "VariableListClickLocation" = "VARIABLE_LIST-CLICK-LOCATION",
  "VariableListClickETicket" = "VARIABLE_LIST-CLICK-ETICKET",
  "VariableListClickViewTickets" = "VARIABLE_LIST-CLICK-VIEW_TICKETS",
  "ContactSelectorClickClose" = "CONTACT_SELECTOR-CLICK-CLOSE",
  "ContactSelectorClickOpen" = "CONTACT_SELECTOR-CLICK-OPEN",
  "ContactSelectorClickSelect" = "CONTACT_SELECTOR-CLICK-SELECT",
  "ContactSelectorEmailEditorClickFilter" = "CONTACT_SELECTOR_EMAIL_EDITOR-CLICK-FILTER",
  "ContactListMessageAll" = "CONTACT_LIST-MESSAGE-ALL",
  "ContactListClickNewContact" = "CONTACT_LIST-CLICK-NEW_CONTACT",
  "ContactListSelectContact" = "CONTACT_LIST-SELECT-CONTACT",
  "ContactListUnSelectContact" = "CONTACT_LIST-UNSELECT-CONTACT",
  "ContactListBulkSelectContact" = "CONTACT_LIST-BULK_SELECT-CONTACT",
  "ContactListBulkUnSelectContact" = "CONTACT_LIST-BULK_UNSELECT-CONTACT",
  "ContactListBulkExport" = "CONTACT_LIST-BULK-EXPORT",
  "ContactListBulkTag" = "CONTACT_LIST-BULK-TAG",
  "ContactListBulkMessage" = "CONTACT_LIST-BULK-MESSAGE",
  "ContactListBulkDelete" = "Bulk Delete Contact Clicked",
  "ContactListBulkPropertyUpdate" = "Bulk Update Contact Property Clicked",
  "ContactListBulkUnsubscribe" = "Bulk Unsubscribe Contact Clicked",
  "ContactListBulkResubscribe" = "Bulk Resubscribe Contact Clicked",
  "ContactListAddTagClicked" = "CONTACT_LIST-ADD-TAG-CLICKED",
  "EventGuestListBulkExport" = "EVENT_GUEST_LIST-BULK-EXPORT",
  "EventGuestListBulkMessage" = "EVENT_GUEST_LIST-BULK-MESSAGE",
  "EventGuestListTableMessage" = "EVENT_GUEST_LIST_TABLE-CLICK-MESSAGE",
  "EventGuestListClickOpenBulkCheckIn" = "EVENT_GUEST_LIST-CLICK-OPEN-BULK-CHECK_IN",
  "EventGuestListBulkCheckInCommandTickets" = "EVENT_GUEST_LIST-BULK-CHECK_IN_COMMAND_TICKETS",
  "EventGuestListClickCancelCommandTickets" = "EVENT_GUEST_LIST-CLICK-CANCEL_COMMAND_TICKETS",
  "EventGuestListSubmitCancelCommandTickets" = "EVENT_GUEST_LIST-SUBMIT-CANCEL_COMMAND_TICKETS",
  "EventGuestListClickCheckInCommandTickets" = "EVENT_GUEST_LIST-CLICK-CHECK_IN_COMMAND_TICKETS",
  "EventGuestListSubmitCheckInCommandTickets" = "EVENT_GUEST_LIST-SUBMIT-CHECK_IN_COMMAND_TICKETS",
  "EventGuestListClickUncheckInCommandTickets" = "EVENT_GUEST_LIST-CLICK-UNCHECK_IN_COMMAND_TICKETS",
  "EventGuestListSubmitUncheckInCommandTickets" = "EVENT_GUEST_LIST-SUBMIT-UNCHECK_IN_COMMAND_TICKETS",
  "EventGuestListClickCancelTicket" = "EVENT_GUEST_LIST-CLICK-CANCEL_TICKET",
  "EventGuestListSubmitCancelTicket" = "EVENT_GUEST_LIST-SUBMIT-CANCEL_TICKET",
  "EventGuestListSubmitCheckInTicket" = "EVENT_GUEST_LIST-SUBMIT-CHECK_IN_TICKET",
  "EventGuestListSubmitUncheckInTicket" = "EVENT_GUEST_LIST-SUBMIT-UNCHECK_IN_TICKET",
  "EventGuestListSelectCommand" = "EVENT_GUEST_LIST-SELECT-COMMAND",
  "BulkAddCancelClicked" = "BULK_ADD_TAG-CANCEL-CLIKED",
  "BulkRemoveCancelClicked" = "BULK_REMOVE_TAG-CANCEL-CLIKED",
  "BulkAddTagCreateNewTag" = "BULK_ADD_TAG-CREATE-NEW-TAG",
  "BulkRemoveTagRemoved" = "BULK_REMOVE_TAG-REMOVED",
  "BulkAddTagAdded" = "BULK_ADD_TAG-ADDED",
  "ContactListRemoveTagClicked" = "CONTACT_LIST-REMOVE-TAG-CLICKED",
  "EmailListClickAll" = "EMAIL_LIST-CLICK-ALL",
  "EmailListClickSend" = "EMAIL_LIST-CLICK-SEND",
  "EmailListClickDraft" = "EMAIL_LIST-CLICK-DRAFT",
  "EmailListClickScheduled" = "EMAIL_LIST-CLICK-SCHEDULED",
  "EmailTableCellRowClickEmailEditor" = "EMAIL_TABLE_CELL_ROW-CLICK-EMAIL_EDITOR",
  "EmailEditorContactListClickEdit" = "EMAIL_EDITOR_CONTACT_LIST-CLICK-EDIT",
  "DetailDonationActionListClickAnnotate" = "DETAIL_DONATION_ACTION_LIST-CLICK-ANNOTATE",
  "DonationAmountEdited" = "Donation Amount Edited",
  "RecurringPaymentStopped" = "Recurring Payment Stopped",
  "RecurringPaymentEndScheduled" = "Recurring Payment End Scheduled",
  "RecurringDonationManageLinkOpened" = "Recurring Donation Manage Link Opened",
  "RecurringDonationLoginLinkSent" = "Recurring Donation Login Link Sent",
  "RefundDonationClicked" = "Refund Donation Clicked",
  "ResendTransactionReceiptClicked" = "Resend Transaction Receipt Clicked",
  "MoveDonationClicked" = "Move Donation Clicked",
  "AdvancedParametersClickEncourageMonthlyDonations" = "ADVANCED_PARAMETERS-CLICK-ENCOURAGE_MONTHLY_DONATIONS",
  "ThankYouPageClick" = "THANK_YOU_PAGE-CLICK",
  "OnboardingProductStep2ClickSmall" = "ONBOARDING_PRODUCT_STEP_2-CLICK-SMALL",
  "OnboardingProductStep2ClickMedium" = "ONBOARDING_PRODUCT_STEP_2-CLICK-MEDIUM",
  "OnboardingProductStep2ClickLarge" = "ONBOARDING_PRODUCT_STEP_2-CLICK-LARGE",
  "OnboardingProductStep2ClickOther" = "ONBOARDING_PRODUCT_STEP_2-CLICK-OTHER",
  "BankClickInviteYourTreasurer" = "BANK-CLICK-INVITE_YOUR_TREASURER",
  "BankConnectionStarted" = "Bank Connection Started",
  "BankConnectionResumed" = "Bank Connection Resumed",
  "BankConnectionOnlineHelpedViewed" = "Bank Connection Online Helped Viewed",
  "ContactPageNewAddNotes" = "CONTACT_PAGE-NEW_ADD_NOTES",
  "ContactPageNoteClickEdit" = "CONTACT_PAGE_NOTE-CLICK-EDIT",
  "ContactPageNoteClickDelete" = "CONTACT_PAGE_NOTE-CLICK-DELETE",
  "PreviewTooltipClickGoToDashboard" = "PREVIEW_TOOLTIP-CLICK-GO_TO_DASHBOARD",
  "DonationDrawerClickGenerateReceipt" = "DONATION_DRAWER-CLICK-GENERATE_RECEIPT",
  "GenerateReceiptTicketingFocusAmount" = "GENERATE_RECEIPT_TICKETING-FOCUS-AMOUNT",
  "GenerateDonationReceiptUncheckSendEmail" = "GENERATE_DONATION_RECEIPT-UNCHECK-SEND_EMAIL",
  "BalanceClickPayoutSchedule" = "BALANCE-CLICK-PAYOUT_SCHEDULE",
  "OrganizationInformationClickAddSignature" = "ORGANIZATION_INFORMATION-CLICK-ADD_SIGNATURE",
  "FormReminderEmailChanged" = "Form Reminder Email Changed",
  "AdvancedParametersClickReminderEmailEdit" = "ADVANCED_PARAMETERS-CLICK-REMINDER_EMAIL_EDIT",
  "EmailDetailDrawerClickSeeMore" = "EMAIL_DETAILS_DRAWER-CLICK-SEE_MORE",
  "EmailDetailDrawerClickSeeLess" = "EMAIL_DETAILS_DRAWER-CLICK-SEE_LESS",
  "DashboardClickMyTransactions" = "DASHBOARD-CLICK-MY_TRANSACTIONS",
  "DashboardClickMyCampaigns" = "DASHBOARD-CLICK-MY_CAMPAIGNS",
  "DonorTransactionClickEdit" = "DONOR_TRANSACTIONS-CLICK-EDIT",
  "DonorTransactionClickSeeMore" = "DONOR_TRANSACTIONS-CLICK-SEE_MORE",
  "DonorTransactionDrawerClickEditTheAmount" = "DONOR_TRANSACTION_DRAWER-CLICK-EDIT_THE_AMOUNT",
  "DonorTransactionDrawerClickStopDonation" = "DONOR_TRANSACTION_DRAWER-CLICK-STOP_DONATION",
  "DonorTransactionDrawerClickEditChargeDate" = "DONOR_TRANSACTION_DRAWER-CLICK-EDIT_CHARGE_DATE",
  "RecurringChargeDateEdited" = "Recurring Charge Date Edited",
  "DonorTransactionDrawerClickPaymentMethodUpdate" = "DONOR_TRANSACTION_DRAWER-CLICK-PAYMENT_METHOD_UPDATE",
  "DonorTransactionDrawerClickDownloadReceipt" = "DONOR_TRANSACTION_DRAWER-CLICK-DOWNLOAD_RECEIPT",
  "DonorTransactionDrawerClickDownloadETicket" = "DONOR_TRANSACTION_DRAWER-CLICK-DOWNLOAD_E_TICKET",
  "DonorCampaignsClickEdit" = "DONOR_CAMPAIGNS-CLICK-EDIT",
  "EmbedPopupLoaded" = "EMBED_POPUP-LOADED",
  "EmbedPopupClick" = "EMBED_POPUP-CLICK",
  "AdministrativeInformationFocusOrganizationName" = "ADMINISTRATIVE_INFORMATION-FOCUS-ORGANIZATION_NAME",
  "AdministrativeInformationFocusBankDescriptor" = "ADMINISTRATIVE_INFORMATION-FOCUS-BANK_DESCRIPTOR",
  "AdministrativeInformationClickOrganizationType" = "ADMINISTRATIVE_INFORMATION-CLICK-ORGANIZATION_TYPE",
  "AdministrativeInformationFocusReferralQuestion" = "ADMINISTRATIVE_INFORMATION-FOCUS-REFERRAL_QUESTION_OTHER",
  "QualificationQuestionAnswered" = "Qualification Question Answered",
  "QualificationPrecisionAnswered" = "Qualification Precision Answered",
  "CopyEmailClicked" = "Copy Email Clicked",
  "PhoneNumberClickCopy" = "PHONE_NUMBER-CLICK-COPY",
  "DefaultAmountChanged" = "Default Amount Changed",
  "DefaultAmountUsed" = "Default Amount Used",
  "TicketCheckAddress" = "TICKETS-CHECK-ADDRESS",
  "CustomizationCheckAddress" = "CUSTOMIZATION-CHECK-ADDRESS",
  "SettingsClickDeleteOrganization" = "SETTINGS-CLICK-DELETE_ORGANIZATION",
  "SettingsClickDeleteUserAccount" = "SETTINGS-CLICK-DELETE_USER_ACCOUNT",
  "SettingsClickTwoFactorAuth" = "SETTINGS-CLICK-TWO_FACTOR_AUTH",
  "SettingsTwoFactorAuthEnable" = "SETTINGS-TWO_FACTOR_AUTH_ENABLE",
  "SettingsTwoFactorAuthDisable" = "SETTINGS-TWO_FACTOR_AUTH_DISABLE",
  "SettingsTwoFactorAuthConfirmDisableByClick" = "SETTINGS-TWO_FACTOR_AUTH_CONFIRM_DISABLE_BY_CLICK",
  "SettingsTwoFactorAuthConfirmDisableByEnterKeyPress" = "SETTINGS-TWO_FACTOR_AUTH_CONFIRM_DISABLE_BY_ENTER_KEY_PRESS",
  "SettingsTwoFactorAuthFocusPasswordToDisable" = "SETTINGS-TWO_FACTOR_AUTH_FOCUS_PASSWORD_TO_DISABLE",
  "SettingsTwoFactorAuthFocusPasswordToDisableAfterError" = "SETTINGS-TWO_FACTOR_AUTH_FOCUS_PASSWORD_TO_DISABLE_AFTER_ERROR",
  "SettingsTwoFactorAuthSendVerifyEmail" = "SETTINGS-TWO_FACTOR_AUTH_SEND_VERIFY_EMAIL",
  "TwoFactorAuthRecoveryCodesCopied" = "Two Factor Auth Recovery Codes Copied",
  "TwoFactorAuthRecoveryCodesSaved" = "Two Factor Auth Recovery Codes Saved",
  "SettingsCloseDeleteUserAccountModal" = "SETTINGS-CLOSE-DELETE_USER_ACCOUNT_MODAL",
  "SettingsCloseTwoFactorAuthModal" = "SETTINGS-CLOSE-TWO_FACTOR_AUTH_MODAL",
  "SettingsClickConfirmDeleteOrganization" = "SETTINGS-CLICK_CONFIRM_DELETE_ORGANIZATION",
  "SettingsClickConfirmDeleteUserAccount" = "SETTINGS-CLICK_CONFIRM_DELETE_USER_ACCOUNT",
  "DetailTicketActionListClickCancelTickets" = "DETAIL_TICKET_ACTION_LIST-CLICK-CANCEL_TICKETS",
  "DetailTicketActionListClickResendTransactionReceipt" = "DETAIL_TICKET_ACTION_LIST-CLICK-RESEND_TRANSACTION_RECEIPT",
  "DetailTicketActionListClickRefundTicket" = "DETAIL_TICKET_ACTION_LIST-CLICK-REFUND_TICKET",
  "DetailTicketActionListClickGenerateReceipt" = "DETAIL_TICKET_ACTION_LIST-CLICK-GENERATE_RECEIPT",
  "PayoutAddAccountantButtonClick" = "PAYOUTS-CLICK-ADD_MY_ACCOUNTANT",
  "CustomizationClickVideo" = "CUSTOMIZATION-CLICK-VIDEO",
  "CustomizationClickBannerUnsplash" = "CUSTOMIZATION-CLICK-BANNER_UNSPLASH",
  "CustomizationClickPreviewReceipt" = "CUSTOMIZATION-CLICK-PREVIEW_RECEIPT",
  "FormTicketPreviewed" = "Form Ticket Previewed",
  "ClickPreview" = "STEP_0-CLICK-CARD",
  "FormSharingClicked" = "Form Sharing Clicked",
  "ThankYouPageSharingClicked" = "Thank You Page Sharing Clicked",
  "ThankYouPageEditClicked" = "Thank You Page Edit Clicked",
  "ThankYouPageMembershipRebuyClicked" = "Thank You Page Membership Rebuy Clicked",
  "ThankYouPageMembershipOverlapViewed" = "Thank You Page Membership Overlap Viewed",
  "ThankYouPageMembershipOverlapClicked" = "Thank You Page Membership Overlap Clicked",
  "ThankYouPageEditCompleted" = "Thank You Page Edit Completed",
  "CostIndicatorClickMoreInfo" = "NEGATIVE_TIP-CLICK-MORE_INFO",
  "FormsListP2pClickExport" = "FORMS_LIST-P2P-CLICK-EXPORT",
  "FormsListP2pClickNewMessage" = "FORMS_LIST-P2P-CLICK-NEW_MESSAGE",
  "MapClickTicketing" = "MAP-CLICK-TICKETING",
  "MapClickOrganization" = "MAP-CLICK-ORGANIZATION",
  "MapClickLogin" = "MAP-CLICK-LOGIN",
  "MapClickSignup" = "MAP-CLICK-SIGN_UP",
  "AdvancedSettingsClickTranslate" = "ADVANCED_SETTINGS-CLICK-TRANSLATE",
  "AdvancedSettingsClickRemoveTranslations" = "ADVANCED_SETTINGS-CLICK-REMOVE-TRANSLATIONS",
  "GeneralInformationClickAddRegistrationProcess" = "GENERAL_INFORMATION-CLICK-ADD_REGISTRATION_PROCESS",
  "SubmitOrgCategoryPopup" = "SUBMIT-ORG_CATEGORY_POP_UP",
  "FormClickPaymentMethodCoverFees" = "FORM-CLICK-PAYMENT_METHOD-COVER_FEES",
  "FormClickPaymentMethodSplitFees" = "FORM-CLICK-PAYMENT_METHOD-SPLIT_FEES",
  "FormClickPaymentMethodNextTime" = "FORM-CLICK-PAYMENT_METHOD-NEXT-TIME",
  "FormShowSplitOrCoverFees" = "FORM-SHOW-SPLIT_OR_COVER_FEES",
  "FormClickCloseSplitOrCoverFees" = "FORM-CLICK-CLOSE-SPLIT_OR_COVER_FEES",
  "TicketClickFundraiserIndividual" = "TICKET-CLICK-FUNDRAISER_INDIVIDUAL",
  "TicketClickFundraiserNewTeam" = "TICKET-CLICK-FUNDRAISER_NEW_TEAM",
  "TicketClickFundraiserJoinTeam" = "TICKET-CLICK-FUNDRAISER_JOIN_TEAM",
  "FormClickSameAsBuyer" = "FORM-CLICK-SAME_AS_BUYER",
  "TicketClickFundraiserPic" = "TICKET-CLICK-FUNDRAISER_PIC",
  "TicketSelectTeam" = "TICKET-SELECT-TEAM",
  "ReferralTabClickSocialCopy" = "REFERRAL_TAB-CLICK-SOCIAL_COPY",
  "ReferralTabClickSocialFacebook" = "REFERRAL_TAB-CLICK-SOCIAL_FACEBOOK",
  "ReferralTabClickSocialX" = "REFERRAL_TAB-CLICK-SOCIAL_X",
  "ReferralTabClickSocialMessenger" = "REFERRAL_TAB-CLICK-SOCIAL_MESSENGER",
  "ReferralTabClickSocialEmail" = "REFERRAL_TAB-CLICK-SOCIAL_EMAIL",
  "ImportStarted" = "Import Started",
  "ImportCardViewed" = "Import Card Viewed",
  "ImportCardDismissed" = "Import Card Dismissed",
  "ImportTypeSelected" = "Import Type Selected",
  "ImportCompleted" = "Import Completed",
  "ImportFileUploaded" = "Import File Uploaded",
  "ImportTemplateDownloaded" = "Import Template Downloaded",
  "ImportCancelled" = "Import Cancelled",
  "ImportSubmitted" = "Import Submitted",
  "ImportStepViewed" = "Import Step Viewed",
  "ImportExited" = "Import Exited",
  "ImportBackClicked" = "Import Back Clicked",
  "ImportSelectFileClicked" = "Import Select File Clicked",
  "ImportFlowSelected" = "Import Flow Selected",
  "ImportWizardStarted" = "Import Wizard Started",
  "ImportWizardActionClicked" = "Import Wizard Action Clicked",
  "ImportWizardCompleted" = "Import Wizard Completed",
  "MailchimpAudienceStepCompleted" = "Mailchimp Audience Step Completed",
  "MailchimpSegmentsStepCompleted" = "Mailchimp Segments Step Completed",
  "ImportIntroductionCompleted" = "Import Introduction Completed",
  "ImportVerificationCompleted" = "Import Verification Completed",
  "ImportSignInReturned" = "Import Sign In Returned",
  "ImportTemplatesStepCompleted" = "Import Templates Step Completed",
  "ImportEmailHistoryStepCompleted" = "Import Email History Step Completed",
  "ImportBannerRefreshClicked" = "Import Banner Refresh Clicked",
  "PaymentListStatusFilterApplied" = "Payment List Status Filter Applied",
  "PaymentListIncompleteFollowUpClicked" = "Payment List Incomplete Follow Up Clicked",
  "PaymentListClickImport" = "PAYMENT_LIST-CLICK-IMPORT",
  "ImportClickDelete" = "IMPORT-CLICK-DELETE",
  "ReceiptsClickCancelReceipt" = "RECEIPTS-CLICK-CANCEL_RECEIPT",
  "ContactPageClickCancelReceipt" = "CONTACT_PAGE-CLICK-CANCEL_RECEIPT",
  "FocusSearchReceiptPage" = "RECEIPT_LIST-FOCUS-SEARCH",
  "FocusDateFilterReceiptPage" = "RECEIPT_LIST-FOCUS-DATE_RANGE",
  "ApplyDateFilterReceiptPage" = "RECEIPT_LIST-SELECT-DATE_RANGE",
  "ClickExportAllReceiptPage" = "RECEIPT_LIST-CLICK-EXPORT_ALL",
  "ClickExportReceiptPage" = "RECEIPT_LIST-CLICK-EXPORT_TAX_REPORT",
  "ClickContactReceiptTable" = "RECEIPT_LIST-CLICK-CONTACT",
  "ClickReceiptLink" = "CLICK-RECEIPT_LINK",
  "FocusDateFilterDonorReport" = "CONTACT_LIST-EXPORT_MODAL-FOCUS-DATE_RANGE",
  "ApplyDateFilterDonorReport" = "CONTACT_LIST-EXPORT_MODAL-DATE_FILTER",
  "ClickExportDonorReport" = "CONTACT_LIST-CLICK-EXPORT_DONOR_REPORT",
  "ClickExportAllContact" = "CONTACT_LIST-CLICK-EXPORT_ALL_CONTACTS ",
  "ClickExportDonorReportSuccessful" = "CONTACT_LIST-EXPORT_DONOR_REPORT-SUCCESSFUL",
  "LoginFocusTwoFactorCode" = "LOGIN-FOCUS-TWO_FACTOR_CODE",
  "LoginFocusTwoFactorCodeAfterError" = "LOGIN-FOCUS-TWO_FACTOR_CODE_AFTER_ERROR",
  "LoginClickLoginTwoFactorCode" = "LOGIN-CLICK-LOGIN_TWO_FACTOR_CODE",
  "UserSignIn2FACodeResent" = "User Sign In 2FA Code Resent",
  "LoginClickShowPassword" = "LOGIN-CLICK-SHOW_PASSWORD ",
  "PasswordResetStarted" = "Password Reset Started",
  "PasswordResetSent" = "Password Reset Sent",
  "PasswordResetCompleted" = "Password Reset Completed",
  "UserSignUpInputChanged" = "User Sign Up Input Changed",
  "UserSignUpPageViewed" = "User Sign Up Page Viewed",
  "UserSignUpSubmitted" = "User Sign Up Submitted",
  "UserSignInSSOCompleted" = "User Sign In SSO Completed",
  "UserSignUpSSOStarted" = "User Sign Up SSO Started",
  "UserSignUpSSOCompleted" = "User Sign Up SSO Completed",
  "UserSignInCompleted" = "User Sign In Completed",
  "UserSignInFailed" = "User Sign In Failed",
  "UserSignIn2FAStarted" = "User Sign In 2FA Started",
  "UserSignedIn" = "User Signed In",
  "SignInCompleted" = "Sign In Completed",
  "SignInSSOError" = "Sign In SSO Error",
  "LoginClickBack" = "LOGIN-CLICK-BACK",
  "LoginFocusWrongEmailEmail" = "LOGIN-FOCUS-WRONG_EMAIL-EMAIL ",
  "LoginFocusPassword2" = "LOGIN-FOCUS-PASSWORD_2",
  "LoginClickLoginResetPassword" = "LOGIN-CLICK-LOGIN_RESET_PASSWORD",
  "LoginClickLoginCheckEmail" = "LOGIN-CLICK-LOGIN_CHECK_EMAIL",
  "LoginClickLoginCheckEmail2" = "LOGIN-CLICK-LOGIN_CHECK_EMAIL_2",
  "LoginDonorClickBackToLogin" = "LOGIN-DONOR-CLICK-BACK_TO_LOGIN",
  "LoginDonorClickResendLink" = "LOGIN-DONOR-CLICK-RESEND_LINK",
  "LoginClickEditEmail" = "LOGIN-CLICK-EDIT_EMAIL",
  "GuestListDrawerViewed" = "Guest List Drawer Viewed",
  "GuestListStatusChanged" = "Guest List Status Changed",
  "GuestListSorted" = "Guest List Sorted",
  "GuestListFiltered" = "Guest List Filtered",
  "CheckInManagerViewed" = "Check-in Manager Viewed",
  "CheckInManagerSharingStarted" = "Check-in Manager Sharing Started",
  "CheckInManagerSharingCompleted" = "Check-in Manager Sharing Completed",
  "CheckInManagerShareQrCodeDisplayed" = "Check-in Manager Share QR Displayed",
  "CheckInManagerAppProTipDisplayed" = "Check-in Manager App Pro Tip Displayed",
  "CheckInManagerAppProTipClicked" = "Check-in Manager App Pro Tip Clicked",
  "CheckInManagerAppProTipDismissed" = "Check-in Manager App Pro Tip Dismissed",
  // Duplicate event requested by Product team
  "FormClickSplitOrCoverFees" = "FORM-CLICK-SPLIT_OR_COVER_FEES",
  "FormClickSubmitPaymentError" = "Donor Form Payment Error",
  "FormClicked" = "Form Clicked",
  "FormOpened" = "Form Opened",
  "FormArchived" = "Form Archived",
  "FormEditClicked" = "Form Edit Clicked",
  "FormDeleted" = "Form Deleted",
  "FormCloned" = "Form Cloned",
  "FormSalesClosed" = "Form Sales Closed",
  "FormSalesOpened" = "Form Sales Opened",
  "FormFundraiserListDownloaded" = "Form Fundraiser List Downloaded",
  "FormGuestListClicked" = "Form Guest List Clicked",
  "FormPaymentListClicked" = "Form Payment List Clicked",
  "QRCodeScanConfirmationPageViewed" = "QR Code Scan Confirmation Page Viewed",
  "QRCodeScanDonorPageViewed" = "QR Code Scan Donor Page Viewed",
  "QRCodeScanCompleted" = "QR Code Scan Completed",
  "QRCodeScanMoreDetailsViewed" = "QR Code Scan More Details Viewed",
  "TicketCheckedIn" = "Ticket Checked In",
  "TicketCheckInFailed" = "Ticket Check-in Failed",
  "EventManagementGuestListTable" = "EVENT-MANAGEMENT-GUEST-LIST-TABLE",
  "EventManagementGuestListClickManageCheckins" = "EVENT-MANAGEMENT-GUEST-LIST-CLICK-MANAGE-CHECKINS",
  "EventManagementGuestListAccessToCheckinManagerFromTour" = "EVENT-MANAGEMENT-GUEST-LIST-ACCESS-TO-CHECKIN-MANAGER-FROM-TOUR",
  "EventManagementGuestListClickOpenCheckinManager" = "EVENT-MANAGEMENT-GUEST-LIST-CLICK-OPEN-CHECKIN-MANAGER",
  "EventManagementGuestListClickShareCheckinManager" = "EVENT-MANAGEMENT-GUEST-LIST-CLICK-SHARE-CHECKIN-MANAGER",
  "EventManagementGuestListClickCopyCheckinManagerLink" = "EVENT-MANAGEMENT-GUEST-LIST-CLICK-COPY-CHECKIN-MANAGER-LINK",
  "EventManagementGuestListClickLinkToLearnMore" = "EVENT-MANAGEMENT-GUEST-LIST-CLICK-LINK-TO-LEARN-MORE",
  "EventManagementCheckinManagerCheckCommand" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-CHECK-COMMAND",
  "EventManagementCheckinManagerUnCheckCommand" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-UNCHECK-COMMAND",
  "EventManagementCheckinManagerCheckTicket" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-CHECK-TICKET",
  "EventManagementCheckinManagerUnCheckTicket" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-UNCHECK-TICKET",
  "EventManagementCheckinManagerClickOpenDetailPanel" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-CLICK-OPEN-DETAIL-PANEL",
  "EventManagementCheckinManagerFocusSearch" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-FOCUS-SEARCH",
  "EventManagementCheckinManagerSearch" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-SEARCH",
  "EventManagementCheckinManagerTourClickNext" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-TOUR-CLICK-NEXT",
  "EventManagementCheckinManagerTourClickBack" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-TOUR-CLICK-BACK",
  "EventManagementCheckinManagerTourClickCloseModal" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-TOUR-CLICK-CLOSE-MODAL",
  "EventManagementCheckinManagerTourClickAccess" = "EVENT-MANAGEMENT-CHECKIN-MANAGER-TOUR-CLICK-ACCESS",
  "PaymentTableClickFormOptions" = "PAYMENT-TABLE-CLICK-FORM_OPTIONS",
  "PaymentTableClickTab" = "PAYMENT_TABLE-CLICK-TAB",
  "PaymentTableClickShare" = "PAYMENT-TABLE-CLICK-SHARE",
  "PaymentTableClickOpenForm" = "PAYMENT-TABLE-CLICK-OPEN_FORM",
  "PaymentTableClickEditForm" = "PAYMENT-TABLE-CLICK-EDIT_FORM",
  "IssuingClickOrderSpendingCard" = "ISSUING-CLICK-ORDER-SPENDING-CARD",
  "IssuingAutomaticRedirectionToSpendingCard" = "Issuing Automatic Redirection To Spending Card",
  "IssuingOnboardingClickSubmitShippingInfos" = "ISSUING-ONBOARDING-CLICK-SUBMIT-SHIPPING-INFOS",
  "IssuingOnboardingClickDisplaySuccessModal" = "ISSUING-ONBOARDING-CLICK-DISPLAY-SUCCESS-MODAL",
  "IssuingClickActivateSpendingCard" = "ISSUING-CLICK-ACTIVATE-SPENDING-CARD",
  "IssuingClickShowPin" = "ISSUING-CLICK-SHOW-PIN",
  "IssuingActivateCardModalClickComeBackLater" = "ISSUING-ACTIVATE-CARD-MODAL-CLICK-COME-BACK-LATER",
  "IssuingActivateCardModalClickActivateCard" = "ISSUING-ACTIVATE-CARD-MODAL-CLICK-ACTIVATE-CARD",
  "IssuingHeaderCardClickOnSchedulePayout" = "ISSUING-HEADER-CARD-CLICK-ON-SCHEDULE-PAYOUT",
  "IssuingSchedulePayoutModalClickSubmitPayout" = "ISSUING-SCHEDULE-PAYOUT-MODAL-CLICK-SUBMIT-PAYOUT",
  "IssuingTopupOpen" = "ISSUING-TOPUP-OPEN",
  "IssuingTopupSubmit" = "ISSUING-TOPUP-SUBMIT",
  "ShowCrossSellInfoBox" = "SHOW-CROSS_SELL_INFO_BOX",
  "ClickCloseCrossSellInfoBox" = "CLICK-CLOSE-CROSS_SELL_INFO_BOX",
  "ClickCTACrossSellInfoBox" = "CLICK-CTA-CROSS_SELL_INFO_BOX",
  "ContactManualCloseClicked" = "Contact Manual Close Clicked",
  "ContactManualAdditionalInformationShowNoteClicked" = "Contact Manual AdditionalInformation Show Note Clicked",
  "ContactManualAdditionalInformationToggleClicked" = "Contact Manual AdditionalInformation Toggle Clicked",
  "ContactManualAddToListClicked" = "Contact Manual Add To List Clicked",
  "ContactManualContactListSelected" = "Contact Manual Contact List Selected",
  "ContactManualContactListUnselected" = "Contact Manual Contact List Unselected",
  "ContactManualSaveAddAnotherClicked" = "Contact Manual Save AddAnother Clicked",
  "ContactManualDetailsNavigateClicked" = "Contact Manual Details Navigate Clicked",
  "DrawerNavigationArrowClicked" = "Drawer Navigation Arrow Clicked",
  "ContactManualAddAnotherClicked" = "Contact Manual AddAnother Clicked",
  "AuctionClickNext" = "AUCTION-CLICK-NEXT",
  "TicketFocusBidAmount" = "TICKET-FOCUS-BID_AMOUNT",
  "ShareFormModalOpen" = "Form Sharing Started",
  "ShareFormModalClickBackBase" = "SHARE_FORM_MODAL-CLICK-BACK_BASE",
  "ShareFormModalClickSeeForm" = "SHARE_FORM_MODAL-CLICK-SEE_FORM",
  "ShareFormModalClickEmbed" = "SHARE_FORM_MODAL-CLICK-EMBED",
  "ShareFormModalClickTab" = "SHARE_FORM_MODAL-CLICK-TAB",
  "ShareFormModalClickChoose" = "SHARE_FORM_MODAL-CLICK-CHOOSE",
  "ShareFormModalClickBackChoose" = "SHARE_FORM_MODAL-CLICK-BACK_CHOOSE",
  "ShareFormModalClickSharingQrCode" = "Form Sharing QR Clicked",
  "ShareFormModalClickCopyLink" = "SHARE_FORM_MODAL-CLICK-COPY_LINK",
  "ShareFormModalClickCopyEmbed" = "Form Sharing Embed Clicked",
  "ShareFormModalClickCopyLinkedButton" = "Form Sharing Linked Button Clicked",
  "PaymentPageNewPostEventThankYouEmail" = "PAYMENT_PAGE-NEW_POST_EVENT_THANK_YOU_EMAIL",
  "PostEventThankYouEmailModalClickClose" = "POST_EVENT_THANK_YOU_EMAIL_MODAL-CLOSED",
  "PostEventThankYouEmailModalClickDismiss" = "POST_EVENT_THANK_YOU_EMAIL_MODAL-DISMISSED",
  "PostEventThankYouEmailModalClickSendEmail" = "POST_EVENT_THANK_YOU_EMAIL_MODAL-EMAIL-SENT",
  "ExtraThankYouEmailAiGenerationCancelled" = "EXTRA_THANK_YOU_EMAIL-AI_GENERATION-CANCELLED",
  "ExtraThankYouEmailAiRegenerationRequested" = "EXTRA_THANK_YOU_EMAIL-AI_REGENERATION-REQUESTED",
  "PaymentNoteAdded" = "Payment Note Added",
  "NewUserInvited" = "New User Invited",
  "ManageCollaboratorsOpened" = "Manage Collaborators Opened",
  "QuickAddClicked" = "Quick Add Clicked",
  "ExistingUserEdited" = "Existing user Edited",
  "ExistingUserMakeAdmin" = "Existing user make admin",
  "ExistingUserSendEvent" = "Existing user send event",
  "ExistingUserRemove" = "Existing user remove",
  "UserAlreadyExistsInFormEditorInviteCollaborator" = "User Already Exists In Form Editor Invite Collaborator",
  "ManagerUsersLearnMoreClicked" = "Manager Users Learn More Clicked",
  "FormChoiceViewed" = "Service Entry Page Viewed",
  "QualificationQuestionsViewed" = "Qualification Questions Viewed",
  "ServiceEntryFormClicked" = "Service Entry Form Clicked",
  "ServiceEntryCreateButtonClicked" = "Service Entry Create Button Clicked",
  "ServiceEntryPreviewViewed" = "Service Entry Preview Viewed",
  "ServiceEntryAssistantUnavailable" = "Service Entry Assistant Unavailable",
  "ServiceEntryAssistantOpened" = "Service Entry Assistant Opened",
  "ServiceEntryAssistantMessageSent" = "Service Entry Assistant Message Sent",
  "ServiceEntryAssistantTypeRecommended" = "Service Entry Assistant Type Recommended",
  "ServiceEntryAssistantCreateClicked" = "Service Entry Assistant Create Clicked",
  "SecondCampaignChecklistViewed" = "Second Campaign Checklist Viewed",
  "SecondCampaignChecklistStepClicked" = "Second Campaign Checklist Step Clicked",
  "RecommendedCampaignTypeViewed" = "Recommended Campaign Type Viewed",
  "RecommendedCampaignTypeClicked" = "Recommended Campaign Type Clicked",
  "RaffleDisclaimerShown" = "Raffle Disclaimer Shown",
  "RaffleDisclaimerAcknowledged" = "Raffle Disclaimer Acknowledged",
  "FormStepChange" = "Form Step Changed",
  "FormStepSubmitted" = "Form Step Submitted",
  "FormImageDeleted" = "Form Image Deleted",
  "FormImageRotated" = "Form Image Rotated",
  "FormImageScaled" = "Form Image Scaled",
  "FormDraftCreated" = "Form Draft Created",
  "FormCreated" = "Form Created",
  "FormImageUploadStarted" = "Form Image Upload Started",
  "FormImageUploadCancelled" = "Form Image Upload Cancelled",
  "FormImageUploadSubmitted" = "Form Image Upload Submitted",
  "FormBannerImageUploaded" = "Form Image Uploaded",
  "FormHEICImageUploadStarted" = "Form HEIC Image Upload Started",
  "FormImageCropped" = "Form Image Cropped",
  "FormImageFitToggled" = "Form Image Fit Toggled",
  "ImageUploaderFlowStarted" = "Image Uploader Flow Started",
  "ImageUploaderFlowCancelled" = "Image Uploader Flow Cancelled",
  "ImageUploaderCropConfirmed" = "Image Uploader Crop Confirmed",
  "ImageUploaderUploadSuccess" = "Image Uploader Upload Success",
  "ImageUploaderUploadFailed" = "Image Uploader Upload Failed",
  "ImageUploaderImageDeleted" = "Image Uploader Image Deleted",
  "ImageUploaderImageRotated" = "Image Uploader Image Rotated",
  "ImageUploaderImageCropped" = "Image Uploader Image Cropped",
  "ImageUploaderFitToggled" = "Image Uploader Fit Toggled",
  "ImageUploaderHEICConversionFailed" = "Image Uploader HEIC Conversion Failed",
  "CustomAssetLibraryImageDeleted" = "Custom Asset Library Image Deleted",
  "CustomAssetLibraryImageSelected" = "Custom Asset Library Image Selected",
  "StepperClickBack" = "STEPPER-CLICK-BACK",
  "ChecklistCreateFormClicked" = "Checklist Create Form Clicked",
  "ChecklistLinkSwiftAidClicked" = "Checklist Link Swift Aid Clicked",
  "ChecklistPreviewClicked" = "Checklist Preview Clicked",
  "ChecklistLinkBankClicked" = "Checklist Link Bank Clicked",
  "ChecklistShareFormClicked" = "Checklist Share Form Clicked",
  "ChecklistAddSignatureClicked" = "Checklist Add Signature Clicked",
  "ChecklistAddSignatureSkipped" = "Checklist Add Signature Skipped",
  "ChecklistViewPayoutsClicked" = "Checklist View Payouts Clicked",
  "ReferralStarted" = "Referral Started",
  "ReferralSharingClicked" = "Referral Sharing Clicked",
  "ReferralOriginSelected" = "Referral Origin Selected",
  "EmailSectionNewMessageClicked" = "Email Section New Message Clicked",
  "FormUnarchived" = "Form Unarchived",
  "FormSuccessModalClicked" = "Form Success Modal Clicked",
  "EmailSectionMessageSent" = "Email Section Message Sent",
  "FormEditionStarted" = "Form Edition Started",
  "EmailRecipientRemoved" = "Email Recipient Removed",
  "EmailRecipientSelected" = "Email Recipient Selected",
  "EmailRecipientAddfromContactsCompleted" = "Email Recipient Add from Contacts Completed",
  "EmailRecipientPasteAttemptedwithError" = "Email Recipient Paste Attempted with Error",
  "EmailImportPasteErrorViewed" = "Email Import Paste Error Viewed",
  "EmailRecipientSelectorViewed" = "Email Recipient Selector Viewed",
  "EmailRecipientSelectorDropdownOpened" = "Email Recipient Selector Dropdown Opened",
  "EmailRecipientSelectorListAdded" = "Email Recipient Selector List Added",
  "EmailRecipientSelectorListRemoved" = "Email Recipient Selector List Removed",
  "EmailRecipientSelectorContactAdded" = "Email Recipient Selector Contact Added",
  "EmailRecipientSelectorContactRemoved" = "Email Recipient Selector Contact Removed",
  "EmailRecipientSelectorImportTriggered" = "Email Recipient Selector Import Triggered",
  "EmailRecipientSelectorFilterTriggered" = "Email Recipient Selector Filter Triggered",
  "EmailRecipientSelectorSearchUsed" = "Email Recipient Selector Search Used",
  "EmailRecipientSelectorNewContactCreated" = "Email Recipient Selector New Contact Created",
  "EmailRecipientPreviewOpened" = "Email Recipient Preview Opened",
  "EmailRecipientPreviewSearchUsed" = "Email Recipient Preview Search Used",
  "EmailRecipientPreviewRowClicked" = "Email Recipient Preview Row Clicked",
  "EligibilityPageViewed" = "Eligibility Page Viewed",
  "EligibilityNonprofitTypeSelected" = "Eligibility Nonprofit Type Selected",
  "EmailDeliveryPageOpenExistingDelivery" = "Email Delivery Page Open Existing Delivery",
  "NewEmailClicked" = "New Email Clicked",
  "EmailChoiceDrawerViewed" = "Email Choice Drawer Viewed",
  "EmailChoiceDrawerTypeSelected" = "Email Choice Drawer Type Selected",
  "EmailPageDuplicateCreated" = "Email Page Duplicate Created",
  "EmailDrawerDuplicateCreated" = "Email Drawer Duplicate Created",
  "EmailDrawerCopyLink" = "Email Drawer Copy Link",
  "EmailPageEmailResultsViewed" = "Email Page Email Results Viewed",
  "ActivationChecklistViewFormClicked" = "Activation Checklist View Form Clicked",
  "ActivationChecklistShareFormClicked" = "Activation Checklist Share Form Clicked",
  "ActivationChecklistWatchDemo" = "Activation Checklist Watch Demo",
  "ActivationChecklistLinkBankClicked" = "Activation Checklist Link Bank Clicked",
  "ActivationChecklistSuccessGotIt" = "Activation Checklist Link Payout Success Got It",
  "TwoFAPrompted" = "2FA Prompted",
  "TwoFASuccessful" = "2FA Successful",
  "TwoFARecoveryCodeClicked" = "2FA Recovery Code Clicked",
  "PasswordCheckSuccessful" = "Password Check Successful",
  "TwoFAConfigurationPrompted" = "2FA Configuration Prompted",
  "TwoFAConfigurationCompleted" = "2FA Configuration Completed",
  "TwoFAConfigurationSkipped" = "2FA Configuration Skipped",
  "EngagementChecklistViewSampleContactClicked" = "Engagement Checklist View Sample Contact Clicked",
  "EngagementChecklistViewContactInfoMarkedAsDone" = "Engagement Checklist View Contact Info Marked as Done",
  "DiscoverContactOptionsClicked" = "Discover Contact Options Clicked",
  "DiscoverContactOptionsMarkedAsDone" = "Discover Contact Options Marked as Done",
  "StartEmailCampaignClicked" = "Start Email Campaign Clicked",
  "StartEmailCampaignMarkedAsDone" = "Start Email Campaign Marked as Done",
  "VerifyEmailClicked" = "Verify Email Clicked",
  "VerifyEmailMarkedAsDone" = "Verify Email Marked as Done",
  "ViewSamplePaymentClicked" = "View Sample Payment Clicked",
  "ViewSamplePaymentMarkedAsDone" = "View Sample Payment Marked as Done",
  "EngagementChecklistAddPaymentClicked" = "Engagement Checklist Add Payment Clicked",
  "EngagementChecklistAddPaymentClickedMarkedAsDone" = "Engagement Checklist Add Payment Clicked Marked as Done",
  "EngagementChecklistAddTeammatesClicked" = "Engagement Checklist Add Teammates Clicked",
  "EngagementChecklistAddTeammatesMarkedAsDone" = "Engagement Checklist Add Teammates Marked as Done",
  "ActivationChecklistNextStepEngageClicked" = "Activation Checklist Next Step Engage Clicked",
  "ActivationChecklistViewPayoutsClicked" = "Activation Checklist View Payouts Clicked",
  "EmailSectionTemplateSelected" = "Email Section Template Selected",
  "FormPreviewed" = "Form Previewed",
  "VisitorsReportUnsubscribed" = "Visitors Report Unsubscribed",
  "FormSharingSendInvitationClicked" = "Form Sharing Send Invitation Clicked",
  "AdvancedSettingsEmailSetupClicked" = "Advanced Settings Email Set Up Clicked",
  "EmailScheduleForLaterStarted" = "Email Schedule for Later Started",
  "EmailScheduleForLaterCompleted" = "Email Schedule for Later Completed",
  "EmailUnscheduled" = "Email Unscheduled",
  "EmailPreviewOpened" = "Email Preview Opened",
  "AddContactStartedFromQuickActions" = "Add Contact Started from Quick Actions",
  "CreateFormStartedFromQuickActions" = "Create Form Started from Quick Actions",
  "FormsNudgeSetupUpEmailInvitationClicked" = "Forms Nudge Set Up Email Invitation Clicked",
  "FormsNudgeSetupUpEmailInvitationDismissed" = "Forms Nudge Set up Email Invitation Dismissed",
  "SuggestedActionDisplayed" = "Suggested Action Displayed",
  "SuggestedActionCTAClicked" = "Suggested Action CTA Clicked",
  "SuggestedActionSkipped" = "Suggested Action Skipped",
  "SuggestedActionSkippedSubmit" = "Suggested Action Skipped Submit",
  "SuggestedActionSkippedClosed" = "Suggested Action Skipped Close",
  "SuggestedFormCommunicationSetupClicked" = "Suggested Form Communication Setup Clicked",
  "ContactSubscriptionManually" = "Contact Subscription Manually",
  "NewBidSumitted" = "TODO",
  "FroalaOptionClicked" = "Froala Option Clicked",
  "TrackBidsClicked" = "Track Bids Clicked",
  "EmailConfirmingScheduledSendClicked" = "Email Confirming Scheduled Send Clicked",
  "OnboardingChecklistDismissed" = "Onboarding Checklist Dismissed",
  "GivingTuesdayPreMadeFormClicked" = "Giving Tuesday Pre Made Form Clicked",
  "CommunicationCardEditClicked" = "Communication Card Edit Clicked",
  "CommunicationCardEmailClicked" = "Communication Card Email Clicked",
  "CommunicationDraftDeleted" = "Communication Draft Deleted",
  "CommunicationTypeChipClicked" = "Communication Type Chip Clicked",
  "CommunicationStatusChipClicked" = "Communication Status Chip Clicked",
  "CommunicationRowClicked" = "Communication Row Clicked",
  "CommunicationSearchUsed" = "Communication Search Used",
  "EmailOpenRatesBannerViewed" = "Email Open Rates Banner Viewed",
  "EmailOpenRatesBannerClosed" = "Email Open Rates Banner Closed",
  "ContactsAddedtoRecipientListStarted" = "Contacts Added to Recipient List Started",
  "ContactsAddedToRecipientListCompleted" = "Contacts Added to Recipient List Completed",
  "CommunicationsClickedFromAdvancedSettings" = "Communications Clicked from Advanced Settings",
  "LearnMoreAboutZeffyViewed" = "Learn More About Zeffy Viewed",
  "FormBackButtonClicked" = "Form Back Button Clicked",
  "FormAddressAdded" = "Form Address Added",
  "FormDescriptionAdded" = "Form Description Added",
  "FormColorChanged" = "Form Color Changed",
  "FormCustomColorCancelled" = "Form Custom Color Cancelled",
  "FormBackgroundStyleChanged" = "Form Background Style Changed",
  "FormDarkModeChanged" = "Form Dark Mode Changed",
  "FormFontStyleChanged" = "Form Font Style Changed",
  "FormDiscountCodeAdded" = "Form Discount Code Added",
  "FormExtraDonationRemoved" = "Form Extra Donation Removed",
  "FormEticketRemoved" = "Form Eticket Removed",
  "FormEticketAdded" = "Form Eticket Added",
  "FormEticketCheckInTooltipDisplayed" = "Form Eticket Check-in Tooltip Displayed",
  "FormEticketCheckInTooltipClicked" = "Form Eticket Check-in Tooltip Clicked",
  "FormEticketCheckInTooltipDismissed" = "Form Eticket Check-in Tooltip Dismissed",
  "CheckInToolsDrawerOpened" = "Check-in Tools Drawer Opened",
  "CheckInToolsDrawerActionClicked" = "Check-in Tools Drawer Action Clicked",
  "PaymentsDrawerOpened" = "Payments Drawer Opened",
  "PaymentsDrawerActionClicked" = "Payments Drawer Action Clicked",
  "FormViewButtonClicked" = "Form View Button Clicked",
  "FormLinkBankButtonClicked" = "Form Link Bank Button Clicked",
  "FormShareButtonClicked" = "Form Share Button Clicked",
  "FormCommunicationClicked" = "Form Communication Clicked",
  "FormThermometerAdded" = "Form Thermometer Added",
  "FormThermometerRemoved" = "Form Thermometer Removed",
  "FormDafPaymentChanged" = "Form DAF Payment Changed",
  "FormChequePaymentAdded" = "Form Cheque Payment Added",
  "FormChequePaymentRemoved" = "Form Cheque Payment Removed",
  "FormTranslationStarted" = "Form Translation Started",
  "FormTranslationAdded" = "Form Translation Added",
  "FormNavStepClicked" = "Form Nav Step Clicked",
  "FormSaveEditClicked" = "Form Save Edit Clicked",
  "FormSaveQuitClicked" = "Form Save Quit Clicked",
  "FormQuitClicked" = "Form Quit Clicked",
  "PaymentSelected" = "Payment Selected",
  "BulkExportCompletedOnCampaignHub" = "Bulk Export Completed on Campaign Hub",
  "OrderFulfillmentStatusChanged" = "Order Fulfillment Status Changed",
  "ItemFulfillmentStatusChanged" = "Item Fulfillment Status Changed",
  "FulfillmentStatusBulkUpdated" = "Fulfillment Status Bulk Updated",
  "BankSetupChoicesViewed" = "Bank Setup Choices Viewed",
  "BankAccountCreationAborted" = "Bank Account Creation Aborted",
  "BankAccountCreationPageViewed" = "Bank Account Creation Page Viewed",
  "BankAccountCreationStarted" = "Bank Account Creation Started",
  "CampaignToDoChecked" = "Campaign To-do Checked",
  "CampaignToDoUnchecked" = "Campaign To-do Unchecked",
  "CampaignTabViewed" = "Campaign Tab Viewed",
  "InsightsTabViewed" = "Insights Tab Viewed",
  "CampaignToDoOpened" = "Campaign To-do Opened",
  "NpoStarterChecklistStepCompleted" = "NPO Starter Checklist Step Completed",
  "NpoStarterChecklistStepStarted" = "NPO Starter Checklist Step Started",
  "NpoStarterChecklistStepReset" = "NPO Starter Checklist Step Reset",
  "TotalRaisedClicked" = "Total Raised Clicked",
  "UserEmailAttachmentTooLarge" = "User Email Attachment Too Large",
  "PayoutReportExported" = "Payout Report Exported",
  "AccountNumberCopied" = "Account Number Copied",
  "UserSignUpRedirectedToSignIn" = "User Sign Up Redirect to Sign In",
  "PaymentExportTypeChanged" = "Payment Export Type Changed",
  "PaymentListExported" = "Payment List Exported",
  "GuestListExported" = "Guest List Exported",
  "PaymentListExportStarted" = "Payment List Export Started",
  "GuestListExportStarted" = "Guest List Export Started",
  "PaymentListExportClosed" = "Payment List Export Closed",
  "PinCopied" = "Pin Copied",
  "PaymentExpressCheckoutLoadedFailed" = "Payment Express Checkout Loaded Failed",
  "PaymentExportSelectAllColumns" = "Payment Export Select All Columns",
  "GuestListExportSelectAllColumns" = "Guest List Export Select All Columns",
  "ShopItemBreakdownExported" = "Shop Item Breakdown Exported",
  "SavedFeeModalCreateFormClicked" = "Saved Fee Modal Create Form Clicked",
  "FundraisingGoalAdded" = "Fundraising Goal Added",
  "FundraisingGoalRemoved" = "Fundraising Goal Removed",
  "OneTimeDonationFrequencyAdded" = "One Time Donation Frequency Added",
  "OneTimeDonationFrequencyRemoved" = "One Time Donation Frequency Removed",
  "WeeklyDonationFrequencyAdded" = "Weekly Donation Frequency Added",
  "WeeklyDonationFrequencyRemoved" = "Weekly Donation Frequency Removed",
  "BiWeeklyDonationFrequencyAdded" = "Bi-Weekly Donation Frequency Added",
  "BiWeeklyDonationFrequencyRemoved" = "Bi-Weekly Donation Frequency Removed",
  "MonthlyDonationFrequencyAdded" = "Monthly Donation Frequency Added",
  "MonthlyDonationFrequencyRemoved" = "Monthly Donation Frequency Removed",
  "QuarterlyDonationFrequencyAdded" = "Quarterly Donation Frequency Added",
  "QuarterlyDonationFrequencyRemoved" = "Quarterly Donation Frequency Removed",
  "YearlyDonationFrequencyAdded" = "Yearly Donation Frequency Added",
  "YearlyDonationFrequencyRemoved" = "Yearly Donation Frequency Removed",
  "DefaultFrequencyEdited" = "Default Frequency Edited",
  "SuggestedAmountsCountEdited" = "Suggested Amounts Count Edited",
  "SpecifyImpactAmountAdded" = "Specify Impact Amount Added",
  "SpecifyImpactAmountRemoved" = "Specify Impact Amount Removed",
  "GenerateTaxReceiptAdded" = "Generate Tax Receipt Added",
  "GenerateTaxReceiptRemoved" = "Generate Tax Receipt Removed",
  "SalesTaxCreated" = "Sales Tax Created",
  "InHonourAdded" = "In Honour Added",
  "InHonourRemoved" = "In Honour Removed",
  "DonorListAdded" = "Donor List Added",
  "DonorListRemoved" = "Donor List Removed",
  "AutomaticEmailDisabled" = "Automatic Email Disabled",
  "AutomaticEmailEnabled" = "Automatic Email Enabled",
  "AutomaticEmailCustomized" = "Automatic Email Customized",
  "AutomaticEmailDateChanged" = "Automatic Email Date Changed",
  "ContactDetailsViewed" = "Contact Details Viewed",
  "CompanyDetailsViewed" = "Company Details Viewed",
  "HouseholdDetailsViewed" = "Household Details Viewed",
  "AIDescriptionLiked" = "AI Generated Description Liked",
  "AIDescriptionDisliked" = "AI Generated Description Disliked",
  "GenerateDescriptionButtonClicked" = "Generate Description Button Clicked",
  "AiDescriptionWizardGenerateClicked" = "AI Description Wizard Generate Clicked",
  "AiDescriptionWizardRegenerateClicked" = "AI Description Wizard Regenerate Clicked",
  "AiDescriptionWizardTabSwitched" = "AI Description Wizard Tab Switched",
  "AiDescriptionWizardStepViewed" = "AI Description Wizard Step Viewed",
  "SaveBannerFromAITab" = "Save Banner From AITab",
  "SignupFormVisited" = "Signup Form Visited",
  "SignupFormSubmitted" = "Signup Form Submitted",
  "BalanceCashBackTileViewed" = "Balance Cash Back Tile Viewed",
  "BalanceCashBackPresentationViewed" = "Balance Cash Back Presentation Viewed",
  "PayoutFrequencySettingsViewed" = "Payout Frequency Settings Viewed",
  "PayoutFrequencyChanged" = "Payout Frequency Changed",
  "DelayPayoutTileViewed" = "Delay Payout Tile Viewed",
  "SignupFormStarted" = "Signup Form Started",
  "SignupFormCreated" = "Signup Form Created",
  "SignupFormEditorStepViewed" = "Signup Form Editor Step Viewed",
  "TemplateDeleted" = "Template Deleted",
  "EmailTemplateStepViewed" = "Email Template Step Viewed",
  "TemplateSelected" = "Template Selected",
  "TemplateCreated" = "Template Created",
  "TemplateUpdated" = "Template Updated",
  "ContactCreated" = "Contact Created",
  "ContactUpdated" = "Contact Updated",
  "ContactDeleted" = "Contact Deleted",
  "CompanyCreated" = "Company Created",
  "CompanyEdited" = "Company Edited",
  "CompanyDeleted" = "Company Deleted",
  "NonprofitInformationHelperViewed" = "Nonprofit Information Helper Viewed",
  "BankVerificationEinSubmitted" = "Bank Verification EIN Submitted",
  "UnsupportedBrowserRedirection" = "Unsupported Browser Redirection",
  "GenerateBannerClicked" = "Generate Banner Clicked",
  "EmailResultsClicked" = "Email Results Clicked",
  "InviteUserDuringActivationClicked" = "Invite User During Activation Clicked",
  "InviteUserDuringActivationInvited" = "Invite User During Activation Invited",
  "InviteUserDuringActivationAccepted" = "Invite User During Activation Accepted",
  "DonorRefundPageViewed" = "Donor Refund Page Viewed",
  "DonorRefundCompleted" = "Donor Refund Completed",
  "DonorFormDescriptionExpanded" = "Donor Form Description Expanded",
  "DonorFormDescriptionDialogOpened" = "Donor Form Description Dialog Opened",
  "DonorFormTicketExpanded" = "Donor Form Ticket Expanded",
  "TransactionDrawerClickPaymentMethodUpdate" = "Transaction Drawer Click Payment Method Update",
  "CustomQuestionChartChanged" = "Custom Question Chart Changed",
  "GuestListFilteredByQuestion" = "Guest List Filtered By Question",
  "PostMailCreationStarted" = "Post Mail Creation Started",
  "PostMailEditionStarted" = "Post Mail Edition Started",
  "PostMailEditionStepViewed" = "Post Mail Edition Step Viewed",
  "PostMailPaymentCompleted" = "Post Mail Payment Completed",
  "PostMailPaymentValidationFailed" = "Post Mail Payment Validation Failed",
  "PostMailPaymentRequested" = "Post Mail Payment Requested",
  "PostMailSentRequestSubmitted" = "Post Mail Sent Request Submitted",
  "PostMailSentRequestFailed" = "Post Mail Sent Request Failed",
  "MobileAppDownloadModalViewed" = "Mobile App Download Modal Viewed",
  "MobileAppDownloadModalClicked" = "Mobile App Download Modal Clicked",
  "MobileAppDownloadModalDismissed" = "Mobile App Download Modal Dismissed",
  "ScanToDownloadAppModalClickAppStore" = "Scan To Download App Modal Click App Store",
  "ScanToDownloadAppModalClickPlayStore" = "Scan To Download App Modal Click Play Store",
  "ScanToDownloadAppModalClosed" = "Scan To Download App Modal Closed",
  "CampaignTodoBannerDisplayed" = "Campaign Todo Banner Displayed",
  "CampaignTodoBannerClicked" = "Campaign Todo Banner Clicked",
  "CampaignTodoBannerDismissed" = "Campaign Todo Banner Dismissed",
  "GuestListBannerDisplayed" = "Guest List Banner Displayed",
  "GuestListBannerClicked" = "Guest List Banner Clicked",
  "GuestListBannerDismissed" = "Guest List Banner Dismissed",
  "EndOfYearChecklistViewed" = "End Of Year Checklist Viewed",
  "EndOfYearChecklistStepClicked" = "End Of Year Checklist Step Clicked",
  "EndOfYearChecklistDismissClicked" = "End Of Year Checklist Dismiss Clicked",
  "EndOfYearChecklistDismissConfirmed" = "End Of Year Checklist Dismiss Confirmed",
  "EmailBuilderStepViewed" = "Email Builder Step Viewed",
  "EmailBuilderCreationStarted" = "Email Builder Creation Started",
  "EmailBuilderEditionStarted" = "Email Builder Edition Started",
  "EmailBuilderSendOrScheduleClicked" = "Email Builder Send or Schedule Clicked",
  "EmailBuilderRecipientsUpdated" = "Email Builder Recipients Updated",
  "EmailBuilderSenderSelected" = "Email Builder Sender Selected",
  "EmailBuilderConvertedTemplateSelected" = "Email Builder Converted Template Selected",
  "EmailBuilderSourceSelected" = "Email Builder Source Selected",
  "EmailBuilderBlockPickerOpened" = "Email Builder Block Picker Opened",
  "EmailBuilderBlockAdded" = "Email Builder Block Added",
  "EmailBuilderPrebuiltBlocksOpened" = "Email Builder Prebuilt Blocks Opened",
  "EmailBuilderPrebuiltBlocksCategoryChanged" = "Email Builder Prebuilt Blocks Category Changed",
  "EmailCreationMethodSelected" = "Email Creation Method Selected",
  "CampaignHubShareCampaignActionClicked" = "Campaign Hub Share Clicked",
  "CampaignHubViewCampaignActionClicked" = "Campaign Hub View Clicked",
  "CampaignHubEditCampaignActionClicked" = "Campaign Hub Edit Clicked",
  "CampaignHubBannerViewed" = "Campaign Hub Banner Viewed",
  "CampaignHubBannerClicked" = "Campaign Hub Banner Clicked",
  "CampaignCardClicked" = "Campaign Card Clicked",
  "CampaignCardViewClicked" = "Campaign Card View Clicked",
  "CampaignCardEditClicked" = "Campaign Card Edit Clicked",
  "CampaignLiveThermometerClicked" = "Campaign Live Thermometer Clicked",
  "FlatFileImportPageReload" = "Flat File Import Page Reload",
  "SideNavigationClickReferral" = "Side Navigation Click Referral",
  "SideNavigationClicked" = "Side Navigation Clicked",
  "TaxReceiptsPageNeedHelpClicked" = "Tax Receipts Page Need Help Clicked",
  "TaxReceiptsPageDownloadAllReceiptsClicked" = "Tax Receipts Page Download All Receipts Clicked",
  "TaxReceiptsPageDownloadSingleReceiptClicked" = "Tax Receipts Page Download Single Receipt Clicked",
  "TaxReceiptsPageDownloadETicketClicked" = "Tax Receipts Page Download E-Ticket Clicked",
  "DonorPaymentExportStarted" = "Donor Payment Export Started",
  "CampaignListEmptyStateCreateFormClicked" = "Campaign List Empty State Create Form Clicked",
  "ViewMyCampaignsButtonClicked" = "View My Campaigns Button Clicked",
  "CrossSellCampaignPageExperiment" = "Cross Sell Banner Viewed",
  "CrossSellCtaClicked" = "Cross Sell Cta Clicked",
  "CrossSellBannerDismissed" = "Cross Sell Banner Dismissed",
  "RecurringTipAdjustmentPageViewed" = "Recurring Tip Adjustment Page Viewed",
  "RecurringTipAdjustmentCompleted" = "Recurring Tip Adjustment Completed",
  "RecurringTipAdjustmentStarted" = "Recurring Tip Adjustment Started",
  "TipRefundStarted" = "Tip Refund Started",
  "HubDateFilterApplied" = "Hub Date Filter Applied",
  "AuctionEndedManually" = "Auction Ended Manually",
  "TicketTransferred" = "Ticket Transferred",
  "CheckInDateFilterApplied" = "Check In Date Filter Applied",
  "EventDateHidden" = "Event Date Hidden",
  "EventDateShown" = "Event Date Shown",
  "MembershipRenewalStarted" = "Membership Renewal Started",
  "MembershipCampaignLifeCycleStepViewed" = "Membership Campaign Life Cycle Step Viewed",
  "MembershipRenewalsMoveToNewCampaignSelected" = "Membership Renewals Move To New Campaign Selected",
  "DisplayOnOrgPageChanged" = "Display On Org Page Changed",
  "IndividualEligibilityFormViewed" = "Individual Eligibility Form Viewed",
  "IndividualEligibilityQuestionAnswered" = "Individual Eligibility Question Answered",
  "IndividualEligibilityFormSubmitted" = "Individual Eligibility Form Submitted",
  "KindbeeRedirectViewed" = "Kindbee Redirect Viewed",
  "KindbeeRedirectClicked" = "Kindbee Redirect Clicked",
  "KindbeeRedirectDismissed" = "Kindbee Redirect Dismissed",
  "G2ReviewPromptShown" = "G2 Review Prompt Shown",
  "G2ReviewPromptDismissed" = "G2 Review Prompt Dismissed",
  "G2ReviewIframeOpened" = "G2 Review Iframe Opened",
  "TicketPageViewed" = "Ticket Page Viewed",
  "GuestInfoSaved" = "Guest Info Saved",
  "TicketLinkCopied" = "Ticket Link Copied",
  "AllTicketsDownloaded" = "All Tickets Downloaded",
  "AddToWalletClicked" = "Add To Wallet Clicked",
  "WalletPassQrScanned" = "Wallet Pass QR Scanned",
  "AttendanceCancelled" = "Attendance Cancelled",
  "QuickBooksPageViewed" = "QuickBooks Page Viewed",
  "QuickBooksConnectStarted" = "QuickBooks Connect Started",
  "QuickBooksConnectCompleted" = "QuickBooks Connect Completed",
  "QuickBooksMappingSaved" = "QuickBooks Mapping Saved",
  "QuickBooksDisconnected" = "QuickBooks Disconnected",
  "QuickBooksBankAccountUpdated" = "QuickBooks Bank Account Updated",
  "EmailSelectedGuestsGuideTriggered" = "Email Selected Guests Guide Triggered",
  "NewsletterCrossSellPageViewed" = "Newsletter Cross Sell Page Viewed",
  "NewsletterCrossSellCreateNewsletterClicked" = "Newsletter Cross Sell Create Newsletter Clicked",
  "ContactEdited" = "Contact Edited",
  "ContactEditionStarted" = "Contact Edition Started",
  "ManageContactPropertiesOpened" = "Manage Contact Properties Opened",
  "ContactCompanyPickerOpened" = "Contact Company Picker Opened",
  "ContactFamilyPickerOpened" = "Contact Family Picker Opened",
  "ContactPropertyVisibilityChanged" = "Contact Property Visibility Changed",
  "ContactPropertyDeleted" = "Contact Property Deleted",
  "CustomPropertyCreationStarted" = "Custom Property Creation Started",
  "CustomPropertyCreated" = "Custom Property Created",
  "CustomPropertyEditionStarted" = "Custom Property Edition Started",
  "CustomPropertyEdited" = "Custom Property Edited",
  "ContactActivityActionClicked" = "Contact Activity Action Clicked",
  "NoteCreated" = "Note Created",
  "NoteEdited" = "Note Edited",
  "NoteDeleted" = "Note Deleted",
  "ContactUnsubscribed" = "Contact Unsubscribed",
  "UnsubscribePageViewed" = "Unsubscribe Page Viewed",
  "SubscriptionPreferencesSaved" = "Subscription Preferences Saved",
  "ContactMerged" = "Contact Merged",
  "DuplicateBannerViewed" = "Duplicate Banner Viewed",
  "ManageDuplicatesModalOpened" = "Manage Duplicates Modal Opened",
  "ContactPairMarkedAsDistinct" = "Contact Pair Marked As Distinct",
  "ContactPairMergeSkipped" = "Contact Pair Merge Skipped",
  "DuplicateListBannerDismissed" = "Duplicate List Banner Dismissed",
  "PotentialDuplicateDetectedOnContactCreation" = "Potential Duplicate Detected On Contact Creation",
  "PotentialDuplicateActionTakenOnContactCreation" = "Potential Duplicate Action Taken On Contact Creation",
  "ReceiptGenerated" = "Receipt Generated",
  "GenerateReceiptChoiceSelected" = "Generate Receipt Choice Selected",
  "ContactCreationStarted" = "Contact Creation Started",
  "CompanyContactAdded" = "Company Contact Added",
  "CompanyContactRemoved" = "Company Contact Removed",
  "HouseholdCreated" = "Household Created",
  "HouseholdMemberAdded" = "Household Member Added",
  "HouseholdMemberRemoved" = "Household Member Removed",
  "SignatureAdded" = "Signature Added",
  "PledgeInvoiceCreationStarted" = "Pledge Invoice Creation Started",
  "PledgeInvoiceDueDateFocused" = "Pledge Invoice Due Date Focused",
  "PledgeInvoiceAutomaticReminderToggled" = "Pledge Invoice Automatic Reminder Toggled",
  "InvoicePageViewed" = "Invoice Page Viewed",
  "InvoicePayNowClicked" = "Invoice Pay Now Clicked",
  "PledgePaymentPageViewed" = "Pledge Payment Page Viewed",
  "PledgePaymentDonorInfoClicked" = "Pledge Payment Donor Info Clicked",
  "PledgePaymentSubmitted" = "Pledge Payment Submitted",
  "PaymentStepPrefillEdited" = "Payment Step Prefill Edited",
  "NextDayPayoutActivated" = "Next Day Payout Activated",
  "NextDayPayoutAccessRequested" = "Next Day Payout Access Requested",
  "ForethoughtWidgetOpened" = "Forethought Widget Opened",
  "DonorSupportChannelResolved" = "Donor Support Channel Resolved",
  "OrganizationSupportChannelResolved" = "Organization Support Channel Resolved",
  "AssistantWidgetOpened" = "Assistant Widget Opened",
  "AssistantWidgetClosed" = "Assistant Widget Closed",
  "AssistantMessageSent" = "Assistant Message Sent",
  "AssistantConversationRestarted" = "Assistant Conversation Restarted",
  "AssistantToolRendered" = "Assistant Tool Rendered",
  "AssistantNavigationClicked" = "Assistant Navigation Clicked",
  "DataAssistantCardViewed" = "Data Assistant Card Viewed",
  "DataAssistantClicked" = "Data Assistant Clicked",
  "DataAssistantQuestionAsked" = "Data Assistant Question Asked",
  "DataAssistantFeedbackSubmitted" = "Data Assistant Feedback Submitted",
  "DataAssistantFeedbackSkipped" = "Data Assistant Feedback Skipped",
  "WorkflowCreationStarted" = "Workflow Creation Started",
  "WorkflowGoalSet" = "Workflow Goal Set",
  "WorkflowEditionEntered" = "Workflow Edition Entered",
  "WorkflowEditionExited" = "Workflow Edition Exited",
  "WorkflowResultsPageViewed" = "Workflow Results Page Viewed",
  "WorkflowEmailStatsClicked" = "Workflow Email Stats Clicked",
  "WorkflowStepAdded" = "Workflow Step Added",
  "WorkflowStepDeleted" = "Workflow Step Deleted",
  "WorkflowPaused" = "Workflow Paused",
  "WorkflowUnpaused" = "Workflow Unpaused",
  CommunicationsChecklistViewed = "Communications Checklist Viewed",
  CommunicationsChecklistStepClicked = "Communications Checklist Step Clicked",
  CommunicationsChecklistHideClicked = "Communications Checklist Hide Clicked",
  CommunicationsChecklistHideConfirmed = "Communications Checklist Hide Confirmed",
}

export type FormVisitParams = { [key: string]: unknown } & {
  has_receipt: boolean | null;
  has_receipt_message: boolean | null;
  has_banner: boolean;
  has_banner_video: boolean;
  has_logo: boolean;
  has_target: boolean | null;
  has_in_honour_option: boolean | null;
  has_a_linked_ticketing: boolean | null;
  has_check_option: boolean | null;
  encourage_monthly_donations: boolean | null;
  organization_country: OrganizationCountry | null;
  count_custom_questions: number | null;
  has_additional_donation: boolean | null;
  locale: Locales;
  form_type: DonationFormCategory | TicketingFormCategory | FormType | null;
  display_address_question: boolean | null;
  has_no_suggested_amount: boolean | null;
  has_preselected_date: boolean;
  is_primary_campaign: boolean | null;
  is_primary_team: boolean | null;
  is_embed?: boolean;
  is_sweepstakes: boolean;
  embed_version?: string | null;
  has_taxes: boolean;
  form_has_addons: boolean;
  available_addons_count: number;
};

export const AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY as string;
export const AMPLITUDE_EXPERIMENT_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENT_API_KEY as string;

export const AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE = 0.02;

export const AMPLITUDE_SESSION_REPLAY_MASK_SELECTORS = [
  "[data-sensitive]",
  '[data-test="form-email"]',
  '[data-test="form-firstname"]',
  '[data-test="form-lastname"]',
  '[data-test="form-phone-number"]',
  '[data-test="form-address"]',
  '[data-test="form-city"]',
  '[data-test="form-postal-code"]',
  '[data-test="form-region"]',
  '[data-test="ticket-form-companyname"]',
  '[data-test^="fulfillment-shipping-"]',
  '[data-test^="question-field"]',
  "#payment-element",
];

export const END_OF_YEAR_CHECKLIST_ENTRY_POINT = "End Of Year Checklist";
